import React, { useEffect, useState, useContext, useRef, useMemo, useCallback } from "react";
import axios from "axios";
import {
  Alert,
  Container,
  Button,
  Col,
  Card,
  Form,
  Row,
  Dropdown,
  Modal,
  Badge,
  OverlayTrigger,
  Tooltip,
  Collapse,
  InputGroup,
} from "react-bootstrap";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEraser,
  faCog,
  faTrash,
  faEnvelope,
  faInfoCircle,
  faExclamationCircle,
  faPencil,
  faRefresh,
  faBan,
  faUndo,
  faPaperPlane as faSend,
  faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useFormik } from "formik";
import Datetime from "react-datetime";
import { Trash, Send, Mail, User } from "react-feather";
import {
  HelpTooltip,
  SelectColumnFilter,
  safeJsonParse,
  normalizeJsonObject,
  getJsonFieldValue,
} from "../../assets/functions.js";
import { ColumnSortingTable } from "../../components/Table.js";
import Loader from "../../components/Loader.js";
import { ShowConfirm } from "../../components/Alerts.js";
import {
  parse,
  isValid,
  format,
  startOfDay,
  subDays,
  addDays,
  endOfDay,
  addMinutes,
  isAfter,
} from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import NotyfContext from "../../contexts/NotyfContext.js";
import useAppDispatch from "../../hooks/useAppDispatch.js";
import useLayout from "../../hooks/useLayout.js";
import { domainConfig } from "../../config.js";
import useAuth from "../../hooks/useAuth.js";
import { eventList } from "../../redux/slices/events.js";
import { Navigate } from "react-router-dom";
import Select from "react-select";
import { getRegistrationTemplateUserTypes, isRegisterTemplateUsed } from "../../utils/registrationEmailTemplates.js";
import EmailTemplateEditorModal from "../../components/EmailTemplateEditorModal.js";
import { UserAdditionalInfoSection } from "../../components/UserAdditionalInfoSection.js";
import { getEventRsvpResponses } from "../../api/rsvp.js";
import {
  filterParticipantRecords,
  participantRecordRows,
  RECORD_RSVP_LABELS,
} from "../../utils/participantRecords.js";
import {
  canAccessEventEmails,
} from "../../utils/eventPermissions.js";

// Helper functions for date calculations and module badges
// Helper function to get default date range (±15 days from today)
const getDefaultDateRangeMonth = () => {
  const now = new Date();
  const endDate = endOfDay(addDays(now, 15));   // 15 days from now
  const startDate = startOfDay(subDays(now, 15)); // 15 days ago
  return { startDate, endDate };
};

// Convert any date value (ISO string, Date object, etc.) to a pt-BR display string
const convertDateToDisplay = (dateValue) => {
  if (!dateValue) return "";
  const normalized = typeof dateValue === "string" && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(dateValue)
    ? `${dateValue.replace(" ", "T")}Z`
    : dateValue;
  const d = new Date(normalized);
  if (!isValid(d)) return "";
  return formatInTimeZone(d, "America/Sao_Paulo", "dd/MM/yyyy HH:mm");
};

const getPreviewContentHash = (value) => {
  let hash = 0;

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }

  return hash.toString(36);
};

const SUPPRESSION_REASON_CONFIG = {
  bounced: { bg: "danger", label: "Rejeitado (bounce)" },
  complained: { bg: "danger", label: "Reclamação (spam)" },
  unsubscribed: { bg: "warning", label: "Descadastrado" },
  manual: { bg: "secondary", label: "Manual" },
};

export function processDataTemplates(dataTable, data, filter) {
  if (!data) return [];
  return data.map((item) => ({
    ...item,
    creation_date_display: convertDateToDisplay(item.creation_date),
  }));
}

export function processDataEmail(dataTable, data, filter) {
  if (!data) return [];
  return data.map((item) => {
    let formattedRetries = [];
    if (item.retried_at) {
      try {
        let parsed = item.retried_at;
        if (typeof parsed === "string") {
          parsed = JSON.parse(parsed);
        }
        if (Array.isArray(parsed)) {
          formattedRetries = parsed.map((rt) => {
            let dateStr = rt;
            if (typeof dateStr === "string" && !dateStr.includes("T") && !dateStr.includes("Z")) {
              dateStr = dateStr.replace(" ", "T") + "Z";
            } else if (typeof dateStr === "string" && !dateStr.includes("Z")) {
              dateStr = dateStr + "Z";
            }
            return formatInTimeZone(new Date(dateStr), "America/Sao_Paulo", "dd/MM/yyyy HH:mm:ss");
          });
        }
      } catch (e) {
        // ignore errors
      }
    }

    return {
      ...item,
      viewed: Number(item.viewed) === 1,
      rsvp_unanswered_only: Number(item.rsvp_unanswered_only) === 1,
      retried_at_formatted: formattedRetries,
      send_date_display: convertDateToDisplay(item.send_date),
      creation_date_display: convertDateToDisplay(item.creation_date),
    };
  });
}

export function processDataBatchEmail(dataTable, data, filter) {
  if (!data) return [];
  return data.map((item) => ({
    ...item,
    rsvp_unanswered_only: Number(item.rsvp_unanswered_only) === 1,
    creation_date_display: convertDateToDisplay(item.creation_date),
    scheduled_for_display: convertDateToDisplay(item.scheduled_for),
    started_date_display: convertDateToDisplay(item.started_date),
    completed_date_display: convertDateToDisplay(item.completed_date),
  }));
}

export function processDataSuppressions(dataTable, data) {
  if (!data) return [];
  return data.map((item) => ({
    ...item,
    created_at_display: convertDateToDisplay(item.created_at),
  }));
}

export const EventEmails = ({
  dataTable,
  updateDataTable,
  setDataTableUpdate,
  setDataTableUpdateWarning,
  onRegisterTemplateVersionChange,
}) => {
  const notyf = useContext(NotyfContext);
  const dispatch = useAppDispatch();
  const userState = useAuth();
  const canViewEmails = canAccessEventEmails(userState, dataTable.platform);

  const { setLoading } = useLayout();
  const [historyDataTemplate, setHistoryDataTemplate] = useState(false);
  const [historyDataEmail, setHistoryDataEmail] = useState(false);
  const [historyDataBatchEmail, setHistoryDataBatchEmail] = useState(false);
  const [historyDataSuppressions, setHistoryDataSuppressions] = useState(false);
  const [updateDataTableWarning, setDataTableUpdateWarningLocal] = useState(false);

  const [filter] = useState({
    dateRange: getDefaultDateRangeMonth(),
  });
  const [resetTrigger, setResetTrigger] = useState(0);

  useEffect(() => {
    if (!canViewEmails) {
      dispatch(eventList({}));
      return;
    }

    const controller = new AbortController();

    if (historyDataTemplate !== false && historyDataEmail !== false && historyDataBatchEmail !== false) {
      setLoading(true);
    }
    axios
      .get(
        `/api/event/all-emails/${dataTable.evento_id}?platform=${dataTable.platform}`,
        { signal: controller.signal },
      )
      .then(function (response) {
        let dataTemplates = processDataTemplates(dataTable, response.data.templates, filter);
        setHistoryDataTemplate(dataTemplates);

        let dataEmail = processDataEmail(dataTable, response.data.allEmails, filter);
        setHistoryDataEmail(dataEmail);

        let dataBatchEmail = processDataBatchEmail(dataTable, response.data.batchEmails, filter);
        setHistoryDataBatchEmail(dataBatchEmail);

        let dataSuppressions = processDataSuppressions(dataTable, response.data.suppressions);
        setHistoryDataSuppressions(dataSuppressions);

        if (updateDataTableWarning === "update") {
          setDataTableUpdateWarningLocal(false);
          if (setDataTableUpdateWarning) {
            setDataTableUpdateWarning(false);
          }
          notyf.open({
            type: "success",
            message: "Dados atualizados com sucesso",
            ripple: true,
            dismissible: true,
          });
        }
        setLoading(false);
      })
      .catch(function (error) {
        if (axios.isCancel(error)) return;

        setLoading(false);
        notyf.open({
          type: "danger",
          message: "Houve um erro, tente novamente",
          ripple: true,
          dismissible: true,
        });
      });

    return () => {
      controller.abort();
      setLoading(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canViewEmails,
    dispatch,
    updateDataTable,
  ]);

  if (!canViewEmails) {
    return <Navigate to="/" />;
  }

  return (
    <Container fluid className="p-0">
      <style>{`
        .nested-modal { z-index: 1060 !important; }
        .nested-modal-backdrop { z-index: 1059 !important; }
      `}</style>
      {historyDataTemplate === false || historyDataEmail === false || historyDataBatchEmail === false || historyDataSuppressions === false ? (
        <div className="py-5">
          <Loader></Loader>
        </div>
      ) : (
        <>
          {/* Templates de e-mail — first */}
          <EmailTemplateSection dataTable={dataTable} dataTemplates={historyDataTemplate} setDataTableUpdate={setDataTableUpdate} onRegisterTemplateVersionChange={onRegisterTemplateVersionChange} />


          {/* Lotes de envio */}
          <EmailBatchesSection dataTable={dataTable} dataBatchEmail={historyDataBatchEmail} dataTemplates={historyDataTemplate} dataEmail={historyDataEmail} setDataTableUpdate={setDataTableUpdate} resetTrigger={resetTrigger} setResetTrigger={setResetTrigger} />

          {/* E-mails individuais */}
          <EmailIndividualSection dataTable={dataTable} dataEmail={historyDataEmail} dataBatchEmail={historyDataBatchEmail} dataTemplates={historyDataTemplate} dataSuppressions={historyDataSuppressions} setDataTableUpdate={setDataTableUpdate} resetTrigger={resetTrigger} setResetTrigger={setResetTrigger} />

          {/* Supressões de e-mail */}
          <EmailSuppressionsSection dataTable={dataTable} dataSuppressions={historyDataSuppressions} setDataTableUpdate={setDataTableUpdate} />

        </>
      )}
    </Container>
  );
};

// ─── CreateBatchModal ─────────────────────────────────────────────────────────
const USER_TYPE_OPTIONS = [
  { value: 0, label: "Participante" },
  { value: 1, label: "Gestor" },
  { value: 2, label: "Expositor" },
];


const reactSelectPortalStyles = {
  control: (provided) => ({ ...provided, padding: 2 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const templateHasRsvp = (template) => Number(template?.rsvp_active) === 1
  || /\{\{rsvp_link_[A-Za-z0-9_-]+\}\}/.test(template?.html || "");
const emailHasBatch = (emailItem) => emailItem?.batch_id != null && Number(emailItem.batch_id) !== 0;

const EmailRsvpReminderOption = ({ template, checked, onChange, id }) => template?.has_rsvp ? (
  <div className="border rounded p-3 mb-4">
    <Form.Check id={id} label="Enviar somente para quem ainda não respondeu ao RSVP" checked={checked} onChange={(event) => onChange(event.target.checked)} />
    <Form.Text>As respostas recebidas por e-mail ou WhatsApp serão verificadas antes do envio. Se houver vários RSVPs, o e-mail será enviado enquanto houver pelo menos um sem resposta, mantendo ativos somente os links ainda não respondidos.</Form.Text>
  </div>
) : null;

const CreateBatchModal = ({ show, onHide, onSave, initialBatch, dataTemplates, dataEmail, dataTable, dataBatchEmail }) => {
  const notyf = useContext(NotyfContext);
  const templates = dataTemplates || [];
  const allEmails = dataEmail || [];

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [rsvpUnansweredOnly, setRsvpUnansweredOnly] = useState(false);
  const [batchName, setBatchName] = useState("");
  // "all" | "filters" | "manual"
  const [audienceMode, setAudienceMode] = useState("all");

  const [sendType, setSendType] = useState("now");
  const [scheduledDate, setScheduledDate] = useState(null);

  // Individual filters (all active simultaneously under "filters" mode)
  const [accessStatusFilter, setAccessStatusFilter] = useState("");
  const [checkinStatusFilter, setCheckinStatusFilter] = useState("");
  const [rsvpChoiceFilter, setRsvpChoiceFilter] = useState("");
  const [emailStatusFilter, setEmailStatusFilter] = useState([]);
  const [userTypeFilter, setUserTypeFilter] = useState([USER_TYPE_OPTIONS[0]]);

  const [jsonFieldFilter, setJsonFieldFilter] = useState("");
  const [jsonValueFilter, setJsonValueFilter] = useState("");

  // Manual selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [tableFilteredParticipants, setTableFilteredParticipants] = useState([]);
  // Ref keeps the latest filtered list without being a reactive dependency
  const tableFilteredParticipantsRef = useRef([]);
  const [participants, setParticipants] = useState([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [participantsError, setParticipantsError] = useState("");
  const [checkinsAvailable, setCheckinsAvailable] = useState(false);
  const [rsvpState, setRsvpState] = useState({ registry: null, loading: false, error: "" });

  // Suppression list fetched on open
  const [suppressions, setSuppressions] = useState([]);
  // Warning state: list of { email, reason } objects that are suppressed in the current audience
  const [suppressedWarning, setSuppressedWarning] = useState(null);

  // Fetch participants and reset state when modal opens
  useEffect(() => {
    let active = true;
    if (show) {
      // Reset
      setParticipants([]);
      setSelectedIds(new Set());
      setTableFilteredParticipants([]);
      setAudienceMode("all");
      setAccessStatusFilter("");
      setCheckinStatusFilter("");
      setRsvpChoiceFilter("");
      setCheckinsAvailable(false);
      setParticipantsError("");
      setRsvpState({ registry: null, loading: true, error: "" });
      setEmailStatusFilter([]);
      setUserTypeFilter([USER_TYPE_OPTIONS[0]]);
      setJsonFieldFilter("");
      setJsonValueFilter("");
      setSendType("now");
      setRsvpUnansweredOnly(Number(initialBatch?.rsvp_unanswered_only) === 1);
      setScheduledDate(new Date(Date.now() + 15 * 60000));
      if (initialBatch) {
        setBatchName(initialBatch.name || "");
        const found = templates.find(
          (t) => String(t.template_id || t.id) === String(initialBatch.template_id),
        );
        setSelectedTemplate(
          found
            ? {
              value: found.template_id || found.id,
              label: found.name || found.nome,
              qrcode: found.qrcode,
              has_rsvp: templateHasRsvp(found),
              subject: found.subject || found.assunto,
              version: found.template_version,
              hasHtml: !!(found.html && found.html.trim().length >= 50),
            }
            : null,
        );
      } else {
        setBatchName("");
        setSelectedTemplate(null);
      }

      // Load the same check-in snapshot and main RSVP registry used by Registros.
      if (dataTable) {
        setParticipantsLoading(true);
        const participantsFetch = axios
          .get(`/api/event/participants/${dataTable.evento_id}`, {
            params: { platform: dataTable.platform },
          })
          .then((res) => {
            const raw = res.data?.participants ?? res.data?.message;
            if (!Array.isArray(raw)) throw new Error("Participants unavailable");
            let data = raw;
            // In resend mode: restrict to participants from the original batch only
            if (initialBatch) {
              const batchUserIds = new Set(
                (dataEmail || [])
                  .filter((e) => String(e.batch_id) === String(initialBatch.batch_id) && e.user_id != null)
                  .map((e) => e.user_id),
              );
              data = data.filter((p) => batchUserIds.has(p.user_id));
            }
            return { data, error: "" };
          })
          .catch(() => ({ data: [], error: "Não foi possível carregar os destinatários." }));

        const checkinsFetch = axios
          .get(`/api/event/checkin/${dataTable.evento_id}`, { params: { checkin_filter: 2 } })
          .then((response) => Array.isArray(response.data?.participants) ? response.data.participants : null)
          .catch(() => null);

        const rsvpFetch = getEventRsvpResponses({ eventId: dataTable.evento_id, pageSize: 1 })
          .then((response) => {
            if (!Array.isArray(response.data?.registry?.definitions)) throw new Error("RSVP registry unavailable");
            return { registry: response.data.registry, loading: false, error: "" };
          })
          .catch((failure) => {
            const registry = failure.response?.data?.code === "RSVP_ATTENDANCE_REQUIRED"
              ? failure.response.data.registry : null;
            return Array.isArray(registry?.definitions)
              ? { registry, loading: false, error: "" }
              : { registry: null, loading: false, error: "Não foi possível carregar a confirmação de presença. O filtro está indisponível." };
          });

        const suppressionsFetch = axios
          .get(`/api/event/email-suppressions/${dataTable.evento_id}`)
          .then((res) => Array.isArray(res.data?.message) ? res.data.message : [])
          .catch(() => []);

        Promise.all([participantsFetch, suppressionsFetch, rsvpFetch, checkinsFetch]).then(([result, supList, rsvp, checkins]) => {
          if (!active) return;
          const checkinsAvailable = Array.isArray(checkins);
          const checkinsByUserId = new Map((checkins || []).map((participant) => [String(participant.user_id), participant]));
          setParticipants(result.data.map((participant) => {
            const checkin = checkinsByUserId.get(String(participant.user_id));
            return {
              ...participant,
              has_checkin: checkin?.has_checkin,
              data_checkin: checkin?.data_checkin,
              checkinDataAvailable: checkinsAvailable && Boolean(checkin),
            };
          }));
          setSelectedIds(new Set(result.data.map((p) => p.user_id)));
          setCheckinsAvailable(checkinsAvailable);
          setParticipantsError(result.error);
          setSuppressions(supList);
          setRsvpState(rsvp);
          setParticipantsLoading(false);
        });
      }
    }
    return () => { active = false; };
  }, [show, initialBatch, templates, dataTable, dataEmail]);

  const attendanceDefinition = useMemo(() => rsvpState.registry?.definitions?.find(
    (definition) => definition.definition_id === rsvpState.registry.attendance_definition_id,
  ) || null, [rsvpState.registry]);
  const rsvpAvailable = !rsvpState.loading && !rsvpState.error;
  const rsvpFilterAvailable = Boolean(attendanceDefinition && rsvpAvailable);
  const recordParticipants = useMemo(() => participantRecordRows(
    participants, attendanceDefinition, true, checkinsAvailable, rsvpAvailable,
  ).map((participant) => {
    const isParticipant = Number(participant.type) === 0;
    return {
      ...participant,
      ...(!isParticipant || !participant.checkinDataAvailable ? {
        record_has_checkin: null,
        record_checkin_status: isParticipant ? "Dados indisponíveis" : "Não se aplica",
      } : {}),
      ...(!rsvpFilterAvailable ? {
        record_rsvp_status: rsvpAvailable ? "Não configurada" : "Dados indisponíveis",
      } : {}),
      ...(!isParticipant ? { record_rsvp_choice: "unavailable", record_rsvp_status: "Não se aplica" } : {}),
    };
  }), [participants, attendanceDefinition, checkinsAvailable, rsvpAvailable, rsvpFilterAvailable]);

  // Safety: Ensure filters have stable types when switching modes
  useEffect(() => {
    if (audienceMode === "filters") {
      if (typeof jsonFieldFilter !== "string") {
        setJsonFieldFilter("");
      }
    }
  }, [audienceMode, jsonFieldFilter]);

  const templateOptions = templates.map((t) => ({
    value: t.template_id || t.id,
    label: t.name || t.nome,
    qrcode: t.qrcode,
    subject: t.subject || t.assunto,
    version: t.template_version || 0.0,
    rsvp_active: t.rsvp_active,
    has_rsvp: templateHasRsvp(t),
    has_html: !!(t.html && t.html.trim().length >= 50),
  }));

  const toggleAll = useCallback(() => {
    const filtered = tableFilteredParticipantsRef.current;
    const next = new Set(selectedIds);
    if (filtered.length > 0 && filtered.every((p) => selectedIds.has(p.user_id))) {
      filtered.forEach(p => next.delete(p.user_id));
    } else {
      filtered.forEach(p => next.add(p.user_id));
    }
    setSelectedIds(next);
  }, [selectedIds]);

  const toggleOne = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Stable callback: updates both the ref (for toggleAll) and the state (for count display)
  const handleFilteredParticipantsChange = useCallback((data) => {
    tableFilteredParticipantsRef.current = data;
    setTableFilteredParticipants(data);
  }, []);

  // Columns for the manual participant selection table
  const manualColumns = useMemo(() => {
    const form = dataTable?.json?.form || {};
    const fieldsByInputID = new Map((Array.isArray(form.fields) ? form.fields : [])
      .filter((field) => field?.inputID !== undefined && field?.inputID !== null)
      .map((field) => [String(field.inputID), field]));
    const shownInputIDs = [...new Set((Array.isArray(form.fields) ? form.fields : [])
      .filter((field) => field?.showInUserReport === true && field.inputID !== undefined && field.inputID !== null)
      .map((field) => String(field.inputID)))];
    const dynamicColumns = shownInputIDs.map((inputID) => fieldsByInputID.get(inputID)).filter(Boolean).map((f) => ({
      Header: f.label,
      id: `json_${f.inputID}`,
      accessor: (row) => {
        const parsed = normalizeJsonObject(row?.json);
        return getJsonFieldValue(parsed[f.inputID]) ?? "-";
      },
    }));

    return [
      {
        id: "select",
        disableSortBy: true,
        disableFilters: true,
        Header: () => {
          const filtered = tableFilteredParticipantsRef.current;
          return (
            <Form.Check
              checked={
                filtered.length > 0 &&
                filtered.every((p) => selectedIds.has(p.user_id))
              }
              onChange={toggleAll}
            />
          );
        },
        Cell: ({ row }) => (
          <Form.Check
            checked={selectedIds.has(row.original.user_id)}
            onChange={() => toggleOne(row.original.user_id)}
            onClick={(e) => e.stopPropagation()}
          />
        ),
      },
      { Header: form.emailLabel || "E-mail", accessor: "email" },
      ...dynamicColumns,
      {
        Header: "Status",
        id: "status",
        Filter: SelectColumnFilter,
        filter: "equals",
        accessor: (row) => (Number(row.status) === 1 ? "Ativo" : "Inativo"),
      },
      {
        Header: "Tipo de usuário",
        id: "type",
        Filter: SelectColumnFilter,
        filter: "equals",
        accessor: (row) =>
          USER_TYPE_OPTIONS.find((o) => o.value === row.type)?.label ?? String(row.type ?? ""),
      },
      {
        Header: "Confirmação de presença",
        accessor: "record_rsvp_status",
        disableFilters: !rsvpFilterAvailable,
        Filter: ({ column: { filterValue, setFilter }, gotoPage }) => (
          <Form.Select className="mt-2" value={filterValue || ""} onChange={(event) => {
            gotoPage?.(0);
            setFilter(event.target.value || undefined);
          }}>
            <option value="">Todos</option>
            {["yes", "unanswered"].map((choice) => (
              <option key={choice} value={RECORD_RSVP_LABELS[choice]}>{RECORD_RSVP_LABELS[choice]}</option>
            ))}
          </Form.Select>
        ),
        filter: "equals",
      },
      {
        Header: "Check-in",
        accessor: "record_checkin_status",
        Filter: SelectColumnFilter,
        filter: "equals",
      },
    ];
  },
    [selectedIds, toggleAll, toggleOne, dataTable, rsvpFilterAvailable],
  );

  const jsonFieldOptions = useMemo(() => {
    return (dataTable?.json?.form?.fields || []).map((f) => ({
      label: `${f.label} (${f.inputID})`,
      value: String(f.inputID),
    }));
  }, [dataTable]);

  // Computed audience — filters are additive (AND logic), each only narrows if active
  const tableDataFiltered = useMemo(() => {
    if (audienceMode === "all") {
      return recordParticipants;
    }

    if (audienceMode === "filters") {
      let candidateSet = new Set(filterParticipantRecords(recordParticipants, {
        checkinStatus: checkinsAvailable ? checkinStatusFilter : "",
        rsvpChoice: rsvpFilterAvailable ? rsvpChoiceFilter : "",
      }).map((participant) => participant.user_id));

      const intersect = (newSet) => {
        candidateSet = new Set([...candidateSet].filter((id) => newSet.has(id)));
      };

      if (accessStatusFilter !== "") {
        intersect(new Set(participants.filter((participant) => String(participant.status) === accessStatusFilter)
          .map((participant) => participant.user_id)));
      }

      // User type — 0 = Participante, 1 = Gestor, 2 = Expositor
      if (userTypeFilter.length > 0) {
        const selectedTypes = new Set(userTypeFilter.map((o) => o.value));
        const s = new Set(
          participants
            .filter((p) => selectedTypes.has(p.type))
            .map((p) => p.user_id),
        );
        intersect(s);
      }

      // Email status — only available in resend mode, scoped to the original batch
      if (emailStatusFilter.length > 0 && initialBatch) {
        const selectedStatuses = new Set(emailStatusFilter.map((o) => o.value));
        const s = new Set(
          allEmails
            .filter(
              (e) =>
                e.user_id != null &&
                String(e.batch_id) === String(initialBatch.batch_id) &&
                selectedStatuses.has(e.status),
            )
            .map((e) => e.user_id),
        );
        intersect(s);
      }

      // JSON custom field value
      if (jsonFieldFilter && jsonValueFilter.trim() !== "") {
        const searchVal = jsonValueFilter.toLowerCase().trim();
        const fieldKey = String(jsonFieldFilter);

        const s = new Set(
          participants.filter((p) => {
            const parsed = normalizeJsonObject(p?.json);
            const fieldValue = getJsonFieldValue(parsed[fieldKey]);
            return fieldValue != null
              && String(fieldValue).toLowerCase().trim() === searchVal;
          }).map((p) => p.user_id)
        );
        intersect(s);
      }

      return recordParticipants.filter(p => candidateSet.has(p.user_id));
    }

    return recordParticipants;
  }, [audienceMode, accessStatusFilter, checkinStatusFilter, rsvpChoiceFilter, checkinsAvailable, rsvpFilterAvailable, recordParticipants, emailStatusFilter, userTypeFilter, jsonFieldFilter, jsonValueFilter, participants, allEmails, initialBatch]);

  const audience = useMemo(() => {
    const visibleIds = audienceMode === "all" ? null
      : new Set(tableFilteredParticipants.map((participant) => participant.user_id));
    return tableDataFiltered.filter((participant) => selectedIds.has(participant.user_id)
      && (!visibleIds || visibleIds.has(participant.user_id))).map((participant) => participant.user_id);
  }, [audienceMode, tableDataFiltered, tableFilteredParticipants, selectedIds]);

  // True when modal was opened for a resend but the original template no longer exists
  const initialTemplateMissing =
    !!initialBatch &&
    templateOptions.length > 0 &&
    !templateOptions.find((t) => String(t.value) === String(initialBatch.template_id));

  // Batches that used the same template (any version)
  const sameTemplateBatch = selectedTemplate
    ? (dataBatchEmail || []).find(
      (b) =>
        String(b.template_id) === String(selectedTemplate.value) &&
        Number(b.template_version).toFixed(1) !== Number(selectedTemplate.version).toFixed(1),
    )
    : null;

  // Exact duplicate: same template AND same version
  const exactBatch = selectedTemplate
    ? (dataBatchEmail || []).find(
      (b) =>
        String(b.template_id) === String(selectedTemplate.value) &&
        Number(b.template_version).toFixed(1) === Number(selectedTemplate.version).toFixed(1),
    )
    : null;

  const canCreate = !!selectedTemplate && selectedTemplate.has_html !== false && audience.length > 0;

  const handleCreate = () => {
    // Validation
    if (
      !selectedTemplate ||
      (audienceMode === "manual" && selectedIds.size === 0) ||
      (audienceMode === "filters" && tableFilteredParticipants.length === 0) ||
      (sendType === "scheduled" && !scheduledDate) ||
      batchName.trim() === ""
    ) {
      if (!selectedTemplate) notyf.error("Por favor, selecione um modelo de e-mail.");
      if (batchName.trim() === "") notyf.error("Insira o título do lote.");
      if (audienceMode === "manual" && selectedIds.size === 0)
        notyf.error("Selecione pelo menos um participante manualmente.");
      if (audienceMode === "filters" && tableFilteredParticipants.length === 0)
        notyf.error("Os filtros atuais retornam 0 destinatários.");
      if (sendType === "scheduled" && !scheduledDate)
        notyf.error("Por favor, selecione uma data válida para agendamento.");
      return;
    }

    if (sendType === "scheduled") {
      if (!scheduledDate || isNaN(new Date(scheduledDate).getTime())) {
        notyf.error("Por favor, selecione uma data e hora válidas para o agendamento.");
        return;
      }
      const fifteenMinsFromNow = new Date(Date.now() + 14 * 60000);
      if (new Date(scheduledDate) < fifteenMinsFromNow) {
        notyf.error("A data de agendamento deve ter pelo menos 15 minutos de antecedência da data atual.");
        return;
      }
    }

    const audienceIds = new Set(audience.map((id) => String(id)));
    const audienceParticipants = participants.filter((p) => audienceIds.has(String(p.user_id)));
    if (selectedTemplate.has_rsvp && audienceParticipants.some((p) => String(p.type) !== "0")) {
      notyf.open({
        type: "warning",
        message: "Este modelo contém RSVP e só pode ser enviado a participantes (tipo 0). Remova gestores e expositores da seleção para continuar.",
        duration: 7000,
        dismissible: true,
      });
      return;
    }

    // Suppression check: block if any audience participant is suppressed
    const suppressedEmailsMap = new Map(
      suppressions.map((s) => [(s.email || "").toLowerCase().trim(), s.reason])
    );
    const blocked = audienceParticipants
      .filter((p) => suppressedEmailsMap.has((p.email || "").toLowerCase().trim()))
      .map((p) => ({ email: p.email, reason: suppressedEmailsMap.get(p.email.toLowerCase().trim()) }));

    if (blocked.length > 0) {
      setSuppressedWarning(blocked);
      return;
    }

    onSave?.({
      templateId: selectedTemplate.value,
      templateVersion: selectedTemplate.version,
      audienceIds: audience,
      isResend: !!initialBatch,
      parentBatchId: initialBatch?.batch_id || null,
      scheduled_for: sendType === "scheduled" ? scheduledDate : null,
      name: batchName,
      rsvpUnansweredOnly: Boolean(selectedTemplate.has_rsvp && rsvpUnansweredOnly),
    });
    onHide();
  };


  return (
    <>
      <Modal show={show} onHide={onHide} fullscreen backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            {initialBatch ? <>Reenviar Lote <br /> <small>
              <span className="opacity-75">
                ID:  {initialBatch.batch_id}
              </span>
            </small></> : "Criar Lote de Envio"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {participantsLoading ? (
            <div className="py-5"><Loader /></div>
          ) : (
            <>
              {initialBatch && (
                <Alert variant="primary" className="p-2 d-flex align-items-center">
                  <Send size={18} className="me-2 flex-shrink-0" />
                  <span>
                    Você está gerando um <strong>reenvio</strong> baseado no lote original{" "}
                    <strong>#{initialBatch.batch_id}</strong>
                  </span>
                </Alert>
              )}

              {/* 1. Template */}
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold d-flex align-items-center">
                  <Badge bg="primary" pill className="me-2">1</Badge>
                  Configurações do lote
                </Form.Label>
                <div className="border rounded p-3">
                  <Row>
                    <Form.Group className="mb-3" as={Col} sm={12} md={5}>
                      <Form.Label className="fw-semibold">Título do lote</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Ex: Confirmação de inscrição (Lote 1)"
                        value={batchName}
                        onChange={(e) => setBatchName(e.target.value)}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3" as={Col} sm={12} md={7}>
                      <Form.Label className="fw-semibold">
                        Modelo a ser enviado
                      </Form.Label>
                      <Form.Select
                        value={selectedTemplate ? selectedTemplate.value : ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          setRsvpUnansweredOnly(false);
                          if (!val) {
                            setSelectedTemplate(null);
                          } else {
                            // Match type implicitly string-based matching of 'value'
                            const opt = templateOptions.find(o => String(o.value) === String(val));
                            setSelectedTemplate(opt || null);
                          }
                        }}
                      >
                        <option value="">Selecione um modelo...</option>
                        {templateOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Row>
                  {initialTemplateMissing && (
                    <Alert variant="danger" className="p-2 d-flex align-items-center">
                      <FontAwesomeIcon icon={faExclamationCircle} className="me-2 flex-shrink-0" />
                      <span>
                        <strong>Modelo removido:</strong> O modelo original usado no lote{" "}
                        <strong>#{initialBatch.batch_id}</strong> foi excluído e não pode ser pré-selecionado.
                        {" "}Selecione outro modelo para continuar o reenvio.
                      </span>
                    </Alert>
                  )}
                  {sameTemplateBatch && !exactBatch && (
                    <Alert variant="primary" className="p-2 d-flex align-items-center">
                      <FontAwesomeIcon icon={faExclamationCircle} className="me-2 flex-shrink-0" />
                      <span>
                        Este modelo já foi usado no lote{" "}
                        <strong>#{sameTemplateBatch.batch_id}</strong> (v{Number(sameTemplateBatch.template_version).toFixed(1)}), mas com uma versão diferente da atual (v{Number(selectedTemplate.version).toFixed(1)}).
                        {" "}Verifique se deseja criar um novo lote com a versão atualizada.
                      </span>
                    </Alert>
                  )}
                  {exactBatch && (
                    <Alert variant="danger" className="p-2 d-flex align-items-center">
                      <FontAwesomeIcon icon={faExclamationCircle} className="me-2 flex-shrink-0" />
                      <span>
                        <strong>Réplica identificada:</strong> Este modelo na versão
                        <strong> v{Number(exactBatch.template_version).toFixed(1)}</strong> já foi usado no lote
                        <strong> #{exactBatch.batch_id} - {EMAIL_STATUS_CONFIG[exactBatch.status]?.label ?? exactBatch.status}</strong>. Criar este lote resultará em um envio idêntico.
                      </span>
                    </Alert>
                  )}
                </div>
              </Form.Group>

              <EmailRsvpReminderOption template={selectedTemplate} checked={rsvpUnansweredOnly} onChange={setRsvpUnansweredOnly} id="batch-rsvp-unanswered-only" />

              {/* 2. Audience */}
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold d-flex align-items-center">
                  <Badge bg="primary" pill className="me-2">2</Badge>
                  Destinatários
                </Form.Label>
                <div className="border rounded p-3">

                  {/* All registered */}
                  <Form.Check
                    type="radio"
                    id="aud-all"
                    label={`Todos os e-mails${participants.length ? ` (${participants.length})` : ""}`}
                    checked={audienceMode === "all"}
                    onChange={() => {
                      setAudienceMode("all");
                      setSelectedIds(new Set(participants.map((p) => p.user_id)));
                    }}
                    className="mb-2"
                  />

                  {/* Use filters */}
                  <Form.Check
                    type="radio"
                    id="aud-filters"
                    label="Utilizar filtros"
                    checked={audienceMode === "filters"}
                    onChange={() => setAudienceMode("filters")}
                    className="mb-2"
                  />
                  {audienceMode === "filters" && (
                    <div className="row mt-2 mb-2">

                      {/* Filter: user type */}
                      <Form.Group className="mb-3" as={Col} xs={12}>
                        <Form.Label className="fw-semibold mb-1">
                          Filtrar por tipo de usuário
                        </Form.Label>
                        <Select
                          isMulti
                          options={USER_TYPE_OPTIONS}
                          value={userTypeFilter}
                          onChange={(opts) => setUserTypeFilter(opts || [])}
                          placeholder="Selecione os tipos..."
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={reactSelectPortalStyles}
                        />
                      </Form.Group>

                      <Form.Group className="mb-3" as={Col} sm={6} md={4} controlId="batch-access-status">
                        <Form.Label className="fw-semibold mb-1">Filtrar por status</Form.Label>
                        <Form.Select value={accessStatusFilter} onChange={(event) => setAccessStatusFilter(event.target.value)}>
                          <option value="">Todos</option>
                          <option value="1">Ativo</option>
                          <option value="0">Inativo</option>
                        </Form.Select>
                      </Form.Group>

                      <Form.Group className="mb-3" as={Col} sm={6} md={4} controlId="batch-rsvp-choice">
                        <Form.Label className="fw-semibold mb-1">Confirmação de presença</Form.Label>
                        <Form.Select
                          value={rsvpFilterAvailable ? rsvpChoiceFilter : ""}
                          onChange={(event) => setRsvpChoiceFilter(event.target.value)}
                          disabled={!rsvpFilterAvailable}
                        >
                          <option value="">Todas as respostas</option>
                          {["yes", "unanswered"].map((choice) => (
                            <option key={choice} value={choice}>{RECORD_RSVP_LABELS[choice]}</option>
                          ))}
                        </Form.Select>
                        {!rsvpFilterAvailable && <Form.Text className={`d-block ${rsvpState.error ? "text-danger" : "text-muted"}`}>
                          {rsvpState.loading ? "Carregando confirmação de presença…" : rsvpState.error
                            || "Nenhum RSVP marcado como confirmação de presença."}
                        </Form.Text>}
                      </Form.Group>

                      <Form.Group className="mb-3" as={Col} sm={6} md={4} controlId="batch-checkin-status">
                        <Form.Label className="fw-semibold mb-1">Filtrar por check-in</Form.Label>
                        <Form.Select
                          value={checkinsAvailable ? checkinStatusFilter : ""}
                          onChange={(event) => setCheckinStatusFilter(event.target.value)}
                          disabled={!checkinsAvailable}
                        >
                          <option value="">Todos</option>
                          <option value="with">Com check-in</option>
                          <option value="without">Sem check-in</option>
                        </Form.Select>
                        {!checkinsAvailable && <Form.Text className="text-muted d-block">
                          {participantsLoading ? "Carregando check-ins…" : "Dados de check-in indisponíveis."}
                        </Form.Text>}
                      </Form.Group>

                      {/* Filter: email status — resend mode only (queries the original batch's email statuses) */}
                      {!!initialBatch && (
                        <Form.Group className="mb-3" as={Col} sm={6} md={4}>
                          <Form.Label className="fw-semibold mb-1 d-flex align-items-center">
                            Filtrar por status do e-mail
                            <OverlayTrigger
                              overlay={
                                <Tooltip>
                                  Filtra os destinatários pelo status que o e-mail deles teve no
                                  lote original <b>#{initialBatch.batch_id}</b>.
                                </Tooltip>
                              }
                            >
                              <span className="ms-1 text-muted" style={{ cursor: "pointer" }}>
                                <FontAwesomeIcon icon={faInfoCircle} size="sm" />
                              </span>
                            </OverlayTrigger>
                          </Form.Label>
                          <Select
                            isMulti
                            options={Object.entries(EMAIL_STATUS_CONFIG).map(([key, cfg]) => ({ value: key, label: cfg.label }))}
                            value={emailStatusFilter}
                            onChange={(opts) => setEmailStatusFilter(opts || [])}
                            placeholder="Todos os status (sem filtro)..."
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={reactSelectPortalStyles}
                          />
                        </Form.Group>
                      )}

                      {/* Filter: JSON Form Fields */}
                      {jsonFieldOptions.length > 0 && (<Form.Group className="mb-3" as={Col} sm={12}>
                        <Form.Label className="fw-semibold mb-2">
                          Filtrar por campo de formulário
                        </Form.Label>
                        <Row>
                          <Col sm={6} md={5} className="mb-2 mb-sm-0">
                            <Form.Select
                              value={jsonFieldFilter}
                              onChange={(e) => setJsonFieldFilter(e.target.value)}
                            >
                              <option value="">Selecione um campo...</option>
                              {jsonFieldOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </Form.Select>
                          </Col>
                          <Col sm={6} md={4}>
                            <Form.Control
                              type="text"
                              placeholder="Valor do campo..."
                              value={jsonValueFilter}
                              onChange={(e) => setJsonValueFilter(e.target.value)}
                              disabled={!jsonFieldFilter}
                            />
                          </Col>
                        </Row>
                      </Form.Group>)}
                    </div>
                  )}

                  <hr className="my-3" />
                  {participantsError && <Alert variant="danger" className="p-2">{participantsError}</Alert>}
                  <div>
                    <ColumnSortingTable
                      key={"manualParticipants"}
                      columns={manualColumns}
                      data={tableDataFiltered || []}
                      title=""
                      getCellProps={(cell) => ({
                        style: { cursor: cell.column.id !== "select" ? "pointer" : undefined },
                        onClick:
                          cell.column.id !== "select"
                            ? () => toggleOne(cell.row.original.user_id)
                            : undefined,
                      })}
                      triggerExport={false}
                      setTriggerExport={() => { }}
                      onFilteredDataChange={handleFilteredParticipantsChange}
                      removeCard
                    />
                  </div>
                </div>
              </Form.Group>

              {/* 3. Schedule */}
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold d-flex align-items-center">
                  <Badge bg="primary" pill className="me-2">3</Badge>
                  Tipo de envio
                </Form.Label>
                <div className="border rounded p-3">
                  <Form.Check
                    type="radio"
                    id="send-now"
                    label="Enviar agora"
                    name="sendType"
                    className="mb-2"
                    checked={sendType === "now"}
                    onChange={() => setSendType("now")}
                  />
                  <Form.Check
                    type="radio"
                    id="send-scheduled"
                    label="Agendar envio"
                    name="sendType"
                    checked={sendType === "scheduled"}
                    onChange={() => setSendType("scheduled")}
                  />

                  {sendType === "scheduled" && (
                    <Form.Group className="mb-3 mt-2" as={Col} sm={12} md={4} lg={2}>
                      <Datetime
                        className="me-2"
                        value={
                          scheduledDate
                            ? format(scheduledDate, "dd/MM/yyyy HH:mm")
                            : ""
                        }
                        onChange={(date) => {
                          if (
                            date &&
                            typeof date === "object" &&
                            date._isValid !== false
                          ) {
                            setScheduledDate(date.toDate());
                          } else {
                            setScheduledDate(null);
                          }
                        }}
                        dateFormat="DD/MM/YYYY"
                        timeFormat="HH:mm"
                        inputProps={{
                          placeholder: "Data do envio - dd/MM/yyyy HH:mm",
                          className: "form-control",
                        }}
                        locale="pt-br"
                      />
                    </Form.Group>
                  )}
                </div>
              </Form.Group>

              {/* 4. Summary */}
              {canCreate && (() => {
                const noEmailCount = audience.filter(
                  (id) => !participants.find((p) => p.user_id === id)?.email
                ).length;
                const audienceModeLabel =
                  audienceMode === "all" ? "Todos os registrados" :
                    audienceMode === "filters" ? "Com filtros" : "Seleção manual";
                return (
                  <div className="rounded border overflow-hidden">
                    <div
                      className="px-3 py-2 fw-semibold border-bottom d-flex align-items-center gap-2"
                      style={{ backgroundColor: "#f8f9fa", fontSize: 13 }}
                    >
                      <Badge bg="primary" pill className="me-1">4</Badge>
                      Resumo do lote
                    </div>
                    <div className="px-3 py-3">
                      <table style={{ width: "100%", fontSize: 13, borderCollapse: "separate", borderSpacing: "0 6px" }}>
                        <tbody>
                          <tr>
                            <td className="text-muted pe-3" style={{ width: 130, verticalAlign: "top" }}>Modelo</td>
                            <td className="fw-semibold">
                              {selectedTemplate.label}
                              <span className="badge bg-primary text-dark ms-2" style={{ fontSize: 10 }}>v.{Number(selectedTemplate.version || 0).toFixed(1)}</span>
                              {selectedTemplate.qrcode ? <span className="badge bg-primary text-dark ms-2" style={{ fontSize: 10 }}>Código QR</span> : null}
                              {selectedTemplate.rsvp_active ? <span className="badge bg-primary text-dark ms-2" style={{ fontSize: 10 }}>RSVP Ativo</span> : null}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted pe-3" style={{ verticalAlign: "top" }}>Assunto</td>
                            <td>
                              {selectedTemplate.subject ? (
                                <span>{selectedTemplate.subject}</span>
                              ) : (
                                <span className="text-danger">
                                  <FontAwesomeIcon icon={faExclamationCircle} className="me-1" />
                                  Assunto não definido — edite o modelo de e-mail antes de enviar
                                </span>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted pe-3" style={{ verticalAlign: "top" }}>Audiência</td>
                            <td>
                              <span className="fw-semibold">{audience.length}</span>
                              {" destinatário"}{audience.length !== 1 ? "s" : ""}
                              <span className="text-muted ms-2" style={{ fontSize: 12 }}>({audienceModeLabel})</span>
                              {noEmailCount > 0 && (
                                <div className="text-warning mt-1" style={{ fontSize: 12 }}>
                                  <FontAwesomeIcon icon={faExclamationCircle} className="me-1" />
                                  {noEmailCount} participante{noEmailCount !== 1 ? "s" : ""} sem e-mail cadastrado {noEmailCount !== 1 ? "serão ignorados" : "será ignorado"} no envio
                                </div>
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleCreate} disabled={!canCreate}>
            {initialBatch ? (
              <>
                <FontAwesomeIcon icon={faRefresh} className="me-2" />
                Reenviar lote ({audience.length} destinatário{audience.length !== 1 ? "s" : ""})
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                Criar lote ({audience.length} destinatário{audience.length !== 1 ? "s" : ""})
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal >

      {/* Suppression warning modal */}
      <Modal
        show={!!suppressedWarning}
        onHide={() => setSuppressedWarning(null)}
        centered
        backdrop="static"
        className="nested-modal"
        backdropClassName="nested-modal-backdrop"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faBan} className="me-2 text-danger" />
            Endereços suprimidos no lote
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            Os seguintes endereços fazem parte do seu lote mas estão na <strong>lista de supressão</strong>.
            Remova-os da lista de supressões antes de criar este lote.
          </p>
          <ul className="list-unstyled mb-0">
            {(suppressedWarning || []).map((item, i) => {
              const cfg = SUPPRESSION_REASON_CONFIG[item.reason] || { bg: "secondary", label: item.reason };
              return (
                <li key={i} className="d-flex align-items-center gap-2 mb-2">
                  <Badge bg={cfg.bg}>{cfg.label}</Badge>
                  <span className="text-break">{item.email}</span>
                </li>
              );
            })}
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSuppressedWarning(null)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const BATCH_STATUS_CONFIG = {
  pending: { bg: "secondary", label: "Pendente" },
  processing: { bg: "warning", label: "Processando" },
  completed: { bg: "success", label: "Concluído" },
  partial: { bg: "warning", label: "Parcial" },
  failed: { bg: "danger", label: "Falhou" },
};

const EMAIL_STATUS_CONFIG = {
  pending: { bg: "secondary", label: "Pendente" },
  sent: { bg: "success", label: "Enviado" },
  delivered: { bg: "success", label: "Entregue" },
  opened: { bg: "primary", label: "Aberto" },
  bounced: { bg: "danger", label: "Devolvido" },
  complained: { bg: "danger", label: "Reclamado" },
  failed: { bg: "danger", label: "Falhou" },
};

const PROVIDER_OUTCOME_CONFIG = {
  submitted_to_ses: { bg: "success", label: "Enviado à Amazon SES" },
  ses_pre_send_rejected: { bg: "danger", label: "Rejeitado antes da entrega pela Amazon SES" },
  queue_validation_failed: { bg: "danger", label: "Falha de validação antes do envio" },
  accepted_by_ses: { bg: "success", label: "Aceito pela Amazon SES" },
  delivered_to_recipient_server: { bg: "success", label: "Entregue ao servidor do destinatário" },
  delivery_delayed: { bg: "warning", label: "Entrega atrasada" },
  recipient_server_rejected: { bg: "danger", label: "Rejeitado pelo servidor do destinatário" },
  rejected_by_ses: { bg: "danger", label: "Rejeitado pela Amazon SES" },
  bounced: { bg: "danger", label: "Devolvido" },
  complained: { bg: "danger", label: "Reclamado" },
  opened: { bg: "primary", label: "Aberto" },
};

const PROVIDER_EVENT_LABELS = {
  Send: "Envio aceito pela Amazon SES",
  Delivery: "Entregue ao servidor do destinatário",
  DeliveryDelay: "Entrega atrasada",
  Reject: "Rejeitado pela Amazon SES",
  Bounce: "Devolvido",
  Complaint: "Reclamado",
  Open: "Aberto",
};

const EMAIL_TYPE_CONFIG = {
  register_panel: "Registro - Painel de controle",
  email_painel_managament: "Envio de email único",
  email_painel_managament_batch: "Envio de email em batch",
};

// ─── BatchEmailsModal ─────────────────────────────────────────────────────────
const BatchEmailsModal = ({ batch, onHide, allEmails }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [visibleCount, setVisibleCount] = useState(10);

  useEffect(() => {
    setIsSearching(true);
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setIsSearching(false);
      setVisibleCount(10);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  if (!batch) return null;

  const emails = (allEmails || []).filter((e) => String(e.batch_id) === String(batch.batch_id));
  const filteredEmails = emails.filter(em => (em.email || "").toLowerCase().includes(debouncedSearchTerm.toLowerCase()));
  const visibleEmails = filteredEmails.slice(0, visibleCount);

  const handleScroll = (e) => {
    const bottom = e.target.scrollHeight - Math.ceil(e.target.scrollTop) <= e.target.clientHeight + 50;
    if (bottom && visibleCount < filteredEmails.length && !isSearching) {
      setVisibleCount(prev => prev + 10);
    }
  };

  const batchStatusCfg = BATCH_STATUS_CONFIG[batch.status] || { bg: "secondary", label: batch.status };

  return (
    <Modal show={!!batch} onHide={onHide} size="lg" centered fullscreen="lg-down">
      <Modal.Header closeButton>
        <Modal.Title>
          Ver detalhes do Lote
          <br />
          <small>
            <span className="opacity-75">
              ID: {batch.batch_id}
            </span>
          </small>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: 600, overflowY: "auto" }} onScroll={handleScroll}>
        <div className="p-3 border rounded mb-4">
          <Row>
            <Col sm={6}>
              <p className="mb-1"><strong>Título:</strong> {batch.name || "N/A"}</p>
              <p className="mb-1"><strong>Status:</strong> <Badge bg={batchStatusCfg.bg}>{batchStatusCfg.label}</Badge></p>
              <p className="mb-1"><strong>Modelo:</strong> {batch.template_name || "N/A"}</p>
              {batch.rsvp_unanswered_only && <p className="mb-1"><strong>RSVP:</strong> Somente quem ainda não respondeu</p>}
              <p className="mb-1"><strong>Versão:</strong> {batch.template_version != null ? `v.${Number(batch.template_version).toFixed(1)}` : "N/A"}</p>
              <p className="mb-1"><strong>E-mail de resposta:</strong> {batch.template_reply_to || "N/A"}</p>
            </Col>
            <Col sm={6}>
              <p className="mb-1"><strong>Criado em:</strong> {batch.creation_date_display || "N/A"}</p>
              <p className="mb-1"><strong>Agendado para:</strong> {batch.scheduled_for_display || "Envio imediato"}</p>
              <p className="mb-1"><strong>Iniciado em:</strong> {batch.started_date_display || "N/A"}</p>
              <p className="mb-1"><strong>Concluído em:</strong> {batch.completed_date_display || "N/A"}</p>
              <p className="mb-1"><strong>Lote pai #:</strong> {batch.parent_batch_id || "N/A"}</p>
            </Col>
          </Row>
          <hr className="my-2" />
          <Row className="mb-2">
            <Col>
              <p className="mb-0"><strong>Total de envios:</strong> {batch.total || 0}</p>
            </Col>
            <Col>
              <p className="mb-0 text-warning"><strong>Pendentes:</strong> {batch.pending || 0}</p>
            </Col>
            <Col>
              <p className="mb-0 text-success"><strong>Sucesso:</strong> {batch.sent || 0}</p>
            </Col>
            <Col>
              <p className="mb-0 text-danger"><strong>Falhas:</strong> {batch.failed || 0}</p>
            </Col>
          </Row>
          {(batch.error) && (
            <>
              <hr className="my-2" />
              <Row>
                <Col>
                  <p className="mb-0 text-danger"><strong>Erro:</strong> {batch.error}</p>
                </Col>
              </Row>
            </>
          )}
        </div>
        <hr></hr>
        <div>
          <h6 className="mb-2">E-mails associados ({emails.length})</h6>

          <div className="mb-3 sticky-top bg-white py-2" style={{ zIndex: 1 }}>
            <Form.Control
              type="text"
              placeholder="Buscar por e-mail..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          {isSearching ? (
            <div className="py-5"><Loader></Loader></div>
          ) : (
            <>
              {emails.length === 0 ? (
                <p className="text-muted text-center py-4">Nenhum e-mail encontrado neste lote.</p>
              ) : filteredEmails.length === 0 ? (
                <p className="text-muted text-center py-4">Nenhum e-mail corresponde à busca.</p>
              ) : (
                <div className="list-group rounded mb-2">
                  {visibleEmails.map((em) => {
                    const cfg = EMAIL_STATUS_CONFIG[em.status] || { bg: "secondary", label: em.status };
                    const outcomeLabel = em.delivery_outcome
                      ? PROVIDER_OUTCOME_CONFIG[em.delivery_outcome]?.label || em.delivery_outcome
                      : null;
                    const hasFailureDetails = Boolean(outcomeLabel || em.provider_diagnostic || em.error);
                    const initial = (em.email || "?").charAt(0).toUpperCase();
                    return (
                      <div key={em.id} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-3">
                        <div className="d-flex align-items-center">
                          <div
                            className="rounded-circle d-flex justify-content-center align-items-center text-white bg-secondary bg-opacity-25 me-3"
                            style={{ width: 42, height: 42, fontSize: "1.1rem", color: "#495057" }}
                          >
                            <span className="text-dark fw-bold">{initial}</span>
                          </div>
                          <div>
                            <div className="text-muted d-flex align-items-center" style={{ fontSize: 13 }}>
                              <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                              {em.email || "Sem e-mail"}
                            </div>
                            {em.type && (
                              <Badge bg="light" text="dark" className="border mt-1">{EMAIL_TYPE_CONFIG[em.type] || em.type}</Badge>
                            )}
                            {em.status === "failed" && (
                              <div className="small text-danger text-break mt-2">
                                {outcomeLabel && <div><strong>Resultado:</strong> {outcomeLabel}</div>}
                                {em.provider_diagnostic && <div><strong>Diagnóstico:</strong> {em.provider_diagnostic}</div>}
                                {em.error && <div><strong>Erro:</strong> {em.error}</div>}
                                {!hasFailureDetails && <div>Não há motivo adicional registrado para esta falha.</div>}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-end d-flex flex-column align-items-end">
                          <Badge bg={cfg.bg} pill className="mb-2 shadow-sm" style={{ fontSize: 11, padding: "0.45em 0.8em" }}>
                            {cfg.label}
                          </Badge>
                          <div className="text-muted" style={{ fontSize: 11 }}>
                            {em.send_date
                              ? `Enviado: ${convertDateToDisplay(em.send_date)}`
                              : em.status === "pending"
                                ? "Aguardando envio"
                                : "Sem data de envio"}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {visibleCount < filteredEmails.length && (
                    <div className="text-center py-3">
                      <Button variant="outline-primary" size="sm" onClick={() => setVisibleCount(prev => prev + 10)}>Carregar mais...</Button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

// ─── EmailBatchesSection ─────────────────────────────────────────────────────
const EmailBatchesSection = ({ dataTable, dataBatchEmail, dataTemplates, dataEmail, setDataTableUpdate, resetTrigger, setResetTrigger }) => {
  const notyf = useContext(NotyfContext);
  const [triggerExport, setTriggerExport] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [showCreateBatch, setShowCreateBatch] = useState(false);
  const [resendBatch, setResendBatch] = useState(null);

  const [filter, setFilter] = useState({
    dateRange: getDefaultDateRangeMonth(),
    statusBatch: "all",
    extraPropsBatches: false,
  });
  const { setLoading } = useLayout();
  const [refreshTriggerLocal, setRefreshTriggerLocal] = useState(0);

  useEffect(() => {
    if (refreshTriggerLocal > 0) {
      setLoading(true);
      const timer = setTimeout(() => setLoading(false), 400);
      return () => clearTimeout(timer);
    }
  }, [refreshTriggerLocal, setLoading]);

  const handleDateRangeChange = (field, value) => {
    setFilter((prev) => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        [field]: value,
      },
    }));
  };

  const deleteBatchItem = useCallback((batchId) => {
    axios
      .delete(`/api/event/email-batches/${batchId}?event_id=${dataTable.evento_id}`)
      .then(() => {
        notyf.success("Lote excluído com sucesso.");
        if (setDataTableUpdate) setDataTableUpdate((prev) => prev + 1);
      })
      .catch((err) => {
        console.error("Error deleting batch:", err);
        notyf.error("Erro ao excluir o lote.");
      });
  }, [notyf, setDataTableUpdate, dataTable.evento_id]);

  const handleDeleteBatch = useCallback((batchId) => {
    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => (
        <ShowConfirm
          title="Confirmar ação"
          description="Deseja remover este lote e todos os e-mails associados? Esta ação não pode ser desfeita."
          action={() => {
            deleteBatchItem(batchId);
            onClose();
          }}
          onClose={onClose}
        />
      ),
    });
  }, [deleteBatchItem]);

  const handleDeleteAllBatches = useCallback(() => {
    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => (
        <ShowConfirm
          title="Confirmar ação"
          description="Deseja remover todos os lotes deste evento e todos os e-mails associados a eles? Os e-mails individuais serão preservados."
          action={async () => {
            onClose();
            setLoading(true);
            try {
              await axios.delete(`/api/event/email-batches?event_id=${dataTable.evento_id}`);
              notyf.success("Todos os lotes e seus e-mails foram excluídos.");
              if (setDataTableUpdate) setDataTableUpdate((prev) => prev + 1);
            } catch (err) {
              notyf.error(err?.response?.data?.message || "Erro ao excluir os lotes.");
            } finally {
              setLoading(false);
            }
          }}
          onClose={onClose}
        />
      ),
    });
  }, [dataTable.evento_id, notyf, setDataTableUpdate, setLoading]);

  const filteredBatchesForTable = useMemo(() => {
    return dataBatchEmail.filter((batch) => {
      if (filter.statusBatch !== "all" && batch.status !== filter.statusBatch) {
        return false;
      }
      if (!batch.creation_date) return true;
      const creationDate = new Date(batch.creation_date);
      if (!isValid(creationDate)) return true;
      if (filter.dateRange.startDate && filter.dateRange.endDate) {
        return (
          creationDate >= filter.dateRange.startDate &&
          creationDate <= filter.dateRange.endDate
        );
      } else if (filter.dateRange.startDate) {
        return creationDate >= filter.dateRange.startDate;
      } else if (filter.dateRange.endDate) {
        return creationDate <= filter.dateRange.endDate;
      }
      return true;
    });
  }, [dataBatchEmail, refreshTriggerLocal]);

  const columns = useMemo(
    () => [
      {
        Header: "Título",
        accessor: "batch_id",
        Cell: ({ row }) => (
          <span>
            {row.original.name ? `${row.original.name} (#${row.original.batch_id})` : row.original.batch_id ? `#${row.original.batch_id}` : ""}
          </span>
        )
      },
      {
        Header: "Modelo de e-mail",
        accessor: "template_name",
        Filter: SelectColumnFilter,
        filter: "equals",
        Cell: ({ row }) => (
          <>
            {row.original.template_name || ""}
            {row.original.template_version != null && (
              <Badge bg="primary" className="ms-2 text-black" style={{ fontSize: 10 }}>
                v.{Number(row.original.template_version).toFixed(1)}
              </Badge>
            )}
          </>
        ),
      },
      {
        Header: "Status",
        id: "status",
        accessor: (row) => BATCH_STATUS_CONFIG[row.status]?.label || row.status,
        Filter: SelectColumnFilter,
        filter: "equals",
        Cell: ({ row }) => {
          const cfg = BATCH_STATUS_CONFIG[row.original.status] || { bg: "secondary", label: row.original.status };
          return <Badge bg={cfg.bg}>{cfg.label}</Badge>;
        },
      },
      {
        Header: "Envios",
        id: "envios",
        accessor: (row) => row.total,
        Cell: ({ row }) => (
          <span>
            <p className="mb-0">Total: {row.original.total}{" "}</p>
            <p className={`mb-0 ${(row.original.pending || 0) > 0 ? "text-warning fw-semibold" : ""}`}>
              Pendentes: {row.original.pending || 0}
            </p>
            <p className={`mb-0 ${(row.original.sent || 0) > 0 ? "text-success" : ""}`}>
              Enviados: {row.original.sent}
            </p>{" "}
            <p className={`mb-0 ${row.original.failed > 0 ? "text-danger fw-semibold" : ""}`}>
              Falhas: {row.original.failed}
            </p>
          </span>
        ),
      },
      { Header: "Criado em", accessor: "creation_date_display" },
      { Header: "Agendado para", accessor: "scheduled_for_display", Cell: ({ value }) => value || "Envio imediato" },
      ...(filter.extraPropsBatches
        ? [
          { Header: "Iniciado em", accessor: "started_date_display" },
          {
            Header: "Lote pai #",
            accessor: "parent_batch_id",
            Cell: ({ value }) => value ? <span>{value}</span> : "",
          },
          {
            Header: "Erro",
            accessor: "error",
            Cell: ({ value }) => value ? (
              <OverlayTrigger overlay={<Tooltip>{value}</Tooltip>}>
                <span className="text-danger" style={{ cursor: "pointer", fontSize: 13 }}>
                  {value.length > 30 ? value.substring(0, 30) + "..." : value}
                </span>
              </OverlayTrigger>
            ) : "",
          },
        ]
        : []),
      { Header: "Concluído em", accessor: "completed_date_display" },
      {
        Header: "",
        id: "icons",
        disableSortBy: true,
        disableFilters: true,
        Cell: ({ row }) => (
          <>
            <OverlayTrigger overlay={<Tooltip>Excluir lote</Tooltip>}>
              <span className="d-inline-block mx-1">
                <Trash
                  role="button"
                  className="align-middle cursor-pointer"
                  size={18}
                  onClick={() => handleDeleteBatch(row.original.batch_id)}
                />
              </span>
            </OverlayTrigger>
            <OverlayTrigger overlay={<Tooltip>Reenviar lote</Tooltip>}>
              <span className="d-inline-block mx-1">
                <Send
                  role="button"
                  className="align-middle cursor-pointer"
                  size={18}
                  onClick={() => setResendBatch(row.original)}
                />
              </span>
            </OverlayTrigger>
            <OverlayTrigger overlay={<Tooltip>Ver detalhes</Tooltip>}>
              <span className="d-inline-block mx-1">
                <Mail
                  role="button"
                  className="align-middle cursor-pointer"
                  size={18}
                  onClick={() => setSelectedBatch(row.original)}
                />
              </span>
            </OverlayTrigger>
          </>
        ),
      },
    ],
    [handleDeleteBatch, filter.extraPropsBatches],
  );

  return (
    <>
      <Form.Group
        as={Col}
        className="textHeaderAplicativo d-flex justify-content-between align-items-center"
      >
        <h5 className="card-title mb-0">
          Lotes de envio ({dataBatchEmail.length})
        </h5>
        <div>
          <Dropdown className="d-inline">
            <Dropdown.Toggle className="btn btn-outline-primary">
              <FontAwesomeIcon icon={faCog} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setTriggerExport(true)}>Exportar</Dropdown.Item>
              <Dropdown.Item
                disabled={!dataBatchEmail.length}
                onClick={handleDeleteAllBatches}
              >
                Excluir todos os lotes
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() =>
                  setFilter((prev) => ({
                    ...prev,
                    extraPropsBatches: !prev.extraPropsBatches,
                  }))
                }
              >
                {!filter.extraPropsBatches
                  ? "Mostrar mais informações"
                  : "Esconder informações extras"}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <Button
            type="button"
            className="btn btn-outline-primary ms-2"
            onClick={() => setShowCreateBatch(true)}
          >
            <FontAwesomeIcon icon={faPlus} />
          </Button>
        </div>
      </Form.Group>
      <h6 className="text-muted mt-3 mx-2">
        Acompanhe os lotes de envio e seus status de processamento
      </h6>

      {/* Filters: Date Range + Batch Status */}
      <Row className="mb-3 mx-0 g-2">
        <Col sm={6} md={3}>
          <InputGroup className="w-100 flex-nowrap">
            <div className="flex-fill">
              <Datetime
                className="w-100"
                value={
                  filter.dateRange.startDate
                    ? format(new Date(filter.dateRange.startDate), "dd/MM/yyyy HH:mm")
                    : ""
                }
                onChange={(date) => {
                  if (date && typeof date === "object" && date._isValid !== false) {
                    handleDateRangeChange("startDate", date.toDate());
                  } else {
                    handleDateRangeChange("startDate", typeof date === "string" ? date : null);
                  }
                }}
                dateFormat="DD/MM/YYYY"
                timeFormat="HH:mm"
                inputProps={{
                  placeholder: "Data Inicial",
                  className: "form-control",
                  style: { borderTopRightRadius: 0, borderBottomRightRadius: 0 }
                }}
                locale="pt-br"
              />
            </div>
            <InputGroup.Text
              className="cursor-pointer"
              onClick={() => setRefreshTriggerLocal(prev => prev + 1)}
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </InputGroup.Text>
          </InputGroup>
        </Col>
        <Col sm={6} md={3}>
          <InputGroup className="w-100 flex-nowrap">
            <div className="flex-fill">
              <Datetime
                className="w-100"
                value={
                  filter.dateRange.endDate
                    ? format(new Date(filter.dateRange.endDate), "dd/MM/yyyy HH:mm")
                    : ""
                }
                onChange={(date) => {
                  if (date && typeof date === "object" && date._isValid !== false) {
                    handleDateRangeChange("endDate", date.toDate());
                  } else {
                    handleDateRangeChange("endDate", typeof date === "string" ? date : null);
                  }
                }}
                dateFormat="DD/MM/YYYY"
                timeFormat="HH:mm"
                inputProps={{
                  placeholder: "Data Final",
                  className: "form-control",
                  style: { borderTopRightRadius: 0, borderBottomRightRadius: 0 }
                }}
                locale="pt-br"
              />
            </div>
            <InputGroup.Text
              className="cursor-pointer"
              onClick={() => setRefreshTriggerLocal(prev => prev + 1)}
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </InputGroup.Text>
          </InputGroup>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Form.Select
            className="form-control w-100"
            value={filter.statusBatch}
            onChange={(e) => {
              setFilter((prev) => ({ ...prev, statusBatch: e.target.value }));
              setRefreshTriggerLocal(prev => prev + 1);
            }}
          >
            <option value="all">Todos os status</option>
            <option value="pending">Pendente</option>
            <option value="processing">Processando</option>
            <option value="completed">Concluído</option>
            <option value="partial">Parcial</option>
            <option value="failed">Falhou</option>
          </Form.Select>
        </Col>
        <Col xs={12} sm={6} md={3} className="d-flex align-items-center my-3 mb-sm-0 mt-sm-auto">
          <button
            className="btn btn-primary w-100 w-sm-auto"
            onClick={() => {
              setFilter({
                statusBatch: "all",
                dateRange: getDefaultDateRangeMonth(),
                extraPropsBatches: false,
              });
              setRefreshTriggerLocal(prev => prev + 1);
              if (setResetTrigger) setResetTrigger(prev => prev + 1);
            }}
            title="Limpar filtros"
          >
            <FontAwesomeIcon icon={faEraser} />
          </button>
        </Col>
      </Row>

      <div className="pb-3">
        <ColumnSortingTable
          key={"columnBatches" + filteredBatchesForTable.length}
          columns={columns}
          title={dataTable.title + " - lotes"}
          data={filteredBatchesForTable}
          getCellProps={(cellinfo) => ({
            className: cellinfo.column.id === "icons" ? "table-icons-triple" : "",
          })}
          triggerExport={triggerExport}
          setTriggerExport={setTriggerExport}
          resetTrigger={resetTrigger}
        />
      </div>
      <BatchEmailsModal batch={selectedBatch} onHide={() => setSelectedBatch(null)} allEmails={dataEmail} />
      <CreateBatchModal
        show={showCreateBatch || !!resendBatch}
        onHide={() => {
          setShowCreateBatch(false);
          setResendBatch(null);
        }}
        initialBatch={resendBatch}
        dataTemplates={dataTemplates}
        dataEmail={dataEmail}
        dataTable={dataTable}
        dataBatchEmail={dataBatchEmail}
        onSave={(data) => {
          const payload = {
            event_id: dataTable.evento_id,
            template_id: data.templateId,
            template_version: data.templateVersion ?? null,
            audience_ids: data.audienceIds,
            parent_batch_id: data.parentBatchId || null,
            scheduled_for: data.scheduled_for || null,
            rsvp_unanswered_only: data.rsvpUnansweredOnly,
            name: data.name || null,
          };

          axios
            .post("/api/event/email-batches", payload)
            .then((res) => {
              notyf.success(data.isResend ? "Lote reenviado com sucesso." : "Lote criado com sucesso.");
              if (setDataTableUpdate) setDataTableUpdate((prev) => prev + 1);
            })
            .catch((err) => {
              console.error(data.isResend ? "Error resending batch:" : "Error creating batch:", err);
              notyf.error(err?.response?.data?.message || (data.isResend ? "Erro ao reenviar o lote." : "Erro ao criar o lote."));
              if (err?.response?.data?.code === "TEMPLATE_VERSION_STALE" && setDataTableUpdate) setDataTableUpdate((prev) => prev + 1);
            });
        }}
      />
    </>
  );
};

// ─── ResendEmailModal ─────────────────────────────────────────────────────────
const ResendEmailModal = ({ emailItem, onHide, dataTable, dataTemplates, dataEmail, dataSuppressions }) => {
  const notyf = useContext(NotyfContext);
  const { setLoading } = useLayout();

  const templates = dataTemplates || [];
  const allEmails = dataEmail || [];

  // Pre-select template from the original email
  const buildOpt = (t) => ({
    value: t.template_id || t.id,
    label: t.name || t.nome,
    qrcode: t.qrcode,
    subject: t.subject || t.assunto,
    version: t.template_version,
    rsvp_active: t.rsvp_active,
    has_rsvp: templateHasRsvp(t),
    has_html: !!(t.html && t.html.trim().length >= 50),
  });

  const templateOptions = templates.map(buildOpt);

  const initialFound = emailItem
    ? templates.find((t) => String(t.template_id || t.id) === String(emailItem.template_id))
    : null;

  const [selectedTemplate, setSelectedTemplate] = useState(initialFound ? buildOpt(initialFound) : null);
  const [rsvpUnansweredOnly, setRsvpUnansweredOnly] = useState(
    emailHasBatch(emailItem) && Number(emailItem?.rsvp_unanswered_only) === 1,
  );

  // Reset when modal opens for a new email item
  useEffect(() => {
    if (!emailItem) return;
    const found = templates.find((t) => String(t.template_id || t.id) === String(emailItem.template_id));
    setSelectedTemplate(found ? buildOpt(found) : null);
    setRsvpUnansweredOnly(emailHasBatch(emailItem) && Number(emailItem.rsvp_unanswered_only) === 1);
  }, [emailItem, templates]);

  if (!emailItem) return null;

  const isBatchEmail = emailHasBatch(emailItem);

  // Template no longer exists
  const initialTemplateMissing =
    emailItem.template_id &&
    templateOptions.length > 0 &&
    !templateOptions.find((t) => String(t.value) === String(emailItem.template_id));

  // Other emails for this same user (to detect duplicates)
  const userEmails = allEmails.filter(
    (e) => String(e.user_id) === String(emailItem.user_id) && String(e.id) !== String(emailItem.id)
  );

  // The selected template is the same template+version as the email being resent
  const isSameAsOriginal =
    selectedTemplate &&
    emailItem.template_version != null &&
    String(selectedTemplate.value) === String(emailItem.template_id) &&
    Number(selectedTemplate.version).toFixed(1) === Number(emailItem.template_version).toFixed(1);

  // Same template, different version — pick the highest version sent
  // Include emailItem itself so its version is considered when looking for the latest
  const sameTemplateSend = selectedTemplate
    ? [...userEmails, emailItem]
      .filter(
        (e) =>
          e.template_version != null &&
          String(e.template_id) === String(selectedTemplate.value) &&
          Number(e.template_version).toFixed(1) !== Number(selectedTemplate.version).toFixed(1)
      )
      .reduce((max, e) => (max === null || Number(e.template_version) > Number(max.template_version) ? e : max), null)
    : null;

  // Exact duplicate found elsewhere in history (same template AND same version)
  const exactSend = selectedTemplate
    ? userEmails.find(
      (e) =>
        e.template_version != null &&
        String(e.template_id) === String(selectedTemplate.value) &&
        Number(e.template_version).toFixed(1) === Number(selectedTemplate.version).toFixed(1)
    )
    : null;

  // True if the selected template is an exact match either via the email itself or history
  const hasExactMatch = isSameAsOriginal || !!exactSend;

  // Suppression check for the resent email
  const emailSuppression = emailItem && dataSuppressions
    ? dataSuppressions.find((s) =>
      (s.email || "").toLowerCase().trim() === (emailItem.email || "").toLowerCase().trim()
    ) ?? null
    : null;

  const canResend = !!selectedTemplate && selectedTemplate.has_html !== false && !emailSuppression;

  const handleResend = async () => {
    if (!canResend) return;
    setLoading(true);
    try {
      await axios.put(`/api/event/email-resend/${emailItem.id}?event_id=${dataTable.evento_id}`, {
        template_id: selectedTemplate.value,
        template_version: selectedTemplate.version,
        rsvp_unanswered_only: Boolean(isBatchEmail && selectedTemplate.has_rsvp && rsvpUnansweredOnly),
      });
      notyf.success("Novo envio adicionado à fila. O histórico original foi preservado.");
      onHide(true); // true = refresh
    } catch (err) {
      notyf.error(err?.response?.data?.message || "Erro ao reenviar e-mail.");
      if (err?.response?.data?.code === "TEMPLATE_VERSION_STALE") onHide(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={!!emailItem} onHide={() => onHide(false)} fullscreen backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          Reenviar e-mail<br />
          <small><span className="opacity-75">ID do e-mail: {emailItem.id}</span></small>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Alert variant="primary" className="p-2 d-flex align-items-center">
          <Send size={18} className="me-2 flex-shrink-0" />
          <span>
            Você está gerando um <strong>reenvio</strong> para{" "}
            <strong>{emailItem.email}</strong>. O envio original será preservado.
            {isBatchEmail ? " Será criado um lote de reenvio vinculado ao lote original." : " Será criado um novo envio individual."}
          </span>
        </Alert>

        {/* 1. Template */}
        <Form.Group className="mb-4">
          <Form.Label className="fw-bold d-flex align-items-center">
            <Badge bg="primary" pill className="me-2">1</Badge>
            Modelos de e-mail
          </Form.Label>
          <Select
            options={templateOptions}
            value={selectedTemplate}
            onChange={(opt) => { setSelectedTemplate(opt); setRsvpUnansweredOnly(false); }}
            placeholder="Clique para selecionar um modelo..."
            isClearable
            menuPortalTarget={document.body}
            menuPosition="fixed"
            formatOptionLabel={(opt) => <span>{opt.label}</span>}
            styles={{
              control: (provided) => ({ ...provided, padding: 4 }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
          {initialTemplateMissing && (
            <Alert variant="danger" className="p-2 d-flex align-items-center">
              <FontAwesomeIcon icon={faExclamationCircle} className="me-2 flex-shrink-0" />
              <span>
                <strong>Modelo removido:</strong> O modelo original usado neste e-mail foi excluído e não pode ser pré-selecionado.
                {" "}Selecione outro modelo para continuar o reenvio.
              </span>
            </Alert>
          )}
          {sameTemplateSend && !hasExactMatch && (
            <Alert variant="primary" className="p-2 d-flex align-items-center">
              <FontAwesomeIcon icon={faExclamationCircle} className="me-2 flex-shrink-0" />
              <span>
                Este modelo já foi enviado para este destinatário (v{Number(sameTemplateSend.template_version).toFixed(1)}),
                mas com uma versão diferente da atual (v{Number(selectedTemplate.version).toFixed(1)}).
                {" "}Verifique se deseja reenviar com a versão atualizada.
              </span>
            </Alert>
          )}
          {hasExactMatch && (
            <Alert variant="danger" className="p-2 d-flex align-items-center">
              <FontAwesomeIcon icon={faExclamationCircle} className="me-2 flex-shrink-0" />
              <span>
                <strong>Réplica identificada:</strong> Este modelo na versão{" "}
                <strong>v{Number(selectedTemplate.version).toFixed(1)}</strong> já foi enviado para este destinatário
                {exactSend ? ` (ID ${exactSend.id} — ${EMAIL_STATUS_CONFIG[exactSend.status]?.label || exactSend.status})` : ` (ID ${emailItem.id} — ${EMAIL_STATUS_CONFIG[emailItem.status]?.label || emailItem.status})`}. Reenviar resultará em um envio idêntico.
              </span>
            </Alert>
          )}
          {emailSuppression && (
            <Alert variant="danger" className="p-2 d-flex align-items-center">
              <FontAwesomeIcon icon={faBan} className="me-2 flex-shrink-0" />
              <span>
                <strong>Atenção:</strong> Este e-mail está na lista de supressão (motivo: <code>{emailSuppression.reason}</code>).
                O envio não é permitido. Remova-o da lista para continuar.
              </span>
            </Alert>
          )}
        </Form.Group>

        {isBatchEmail && (
          <EmailRsvpReminderOption
            template={selectedTemplate}
            checked={rsvpUnansweredOnly}
            onChange={setRsvpUnansweredOnly}
            id="resend-rsvp-unanswered-only"
          />
        )}

        {/* 2. Audience */}
        <Form.Group className="mb-4">
          <Form.Label className="fw-bold d-flex align-items-center">
            <Badge bg="primary" pill className="me-2">2</Badge>
            Destinatário
          </Form.Label>
          <div className="border rounded p-3 d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faEnvelope} className="text-muted" />
            <span className="fw-semibold">{emailItem.email}</span>
          </div>
        </Form.Group>

        {/* 3. Summary */}
        {canResend && (() => {
          return (
            <div className="rounded border overflow-hidden">
              <div
                className="px-3 py-2 fw-semibold border-bottom d-flex align-items-center gap-2"
                style={{ backgroundColor: "#f8f9fa", fontSize: 13 }}
              >
                <Badge bg="primary" pill className="me-1">3</Badge>
                Resumo do envio
              </div>
              <div className="px-3 py-3">
                <table style={{ width: "100%", fontSize: 13, borderCollapse: "separate", borderSpacing: "0 6px" }}>
                  <tbody>
                    <tr>
                      <td className="text-muted pe-3" style={{ width: 130, verticalAlign: "top" }}>Modelo</td>
                      <td className="fw-semibold">
                        {selectedTemplate.label}
                        <span className="badge bg-primary text-dark ms-2" style={{ fontSize: 10 }}>v.{Number(selectedTemplate.version || 0).toFixed(1)}</span>
                        {selectedTemplate.qrcode ? <span className="badge bg-primary text-dark ms-2" style={{ fontSize: 10 }}>Código QR</span> : null}
                        {selectedTemplate.rsvp_active ? <span className="badge bg-primary text-dark ms-2" style={{ fontSize: 10 }}>RSVP Ativo</span> : null}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted pe-3" style={{ verticalAlign: "top" }}>Assunto</td>
                      <td>
                        {selectedTemplate.subject ? (
                          <span>{selectedTemplate.subject}</span>
                        ) : (
                          <span className="text-danger">
                            <FontAwesomeIcon icon={faExclamationCircle} className="me-1" />
                            Assunto não definido — edite o modelo de e-mail antes de enviar
                          </span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted pe-3" style={{ verticalAlign: "top" }}>Destinatário</td>
                      <td>
                        <span className="fw-semibold">1</span> destinatário
                        <span className="text-muted ms-2" style={{ fontSize: 12 }}>({emailItem.email})</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={() => onHide(false)}>Cancelar</Button>
        <Button variant="primary" onClick={handleResend} disabled={!canResend}>
          <FontAwesomeIcon icon={faRefresh} className="me-2" />
          Reenviar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ─── SendIndividualEmailModal ─────────────────────────────────────────────────────
const SendIndividualEmailModal = ({ show, onHide, dataTable, dataTemplates, dataEmail, onSave, onTemplateStale }) => {
  const notyf = useContext(NotyfContext);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [checkingSuppression, setCheckingSuppression] = useState(false);
  const [selectedParticipantSuppression, setSelectedParticipantSuppression] = useState(null);

  useEffect(() => {
    if (!show) return;
    setSelectedParticipant(null);
    setSelectedTemplate(null);
    setLoading(true);

    const participantsFetch = axios
      .get(`/api/event/participants/${dataTable.evento_id}?platform=${dataTable.platform}`)
      .then((res) => {
        const raw = res.data?.participants ?? res.data?.message;
        return Array.isArray(raw) ? raw : [];
      })
      .catch(() => []);

    participantsFetch.then((parts) => {
      setParticipants(parts);
      setLoading(false);
    });
  }, [show, dataTable.evento_id, dataTable.platform]);

  useEffect(() => {
    if (!selectedParticipant) {
      setSelectedParticipantSuppression(null);
      return;
    }
    setCheckingSuppression(true);
    axios.get(`/api/event/email-suppressions/${dataTable.evento_id}?email=${encodeURIComponent(selectedParticipant.email)}`)
      .then(res => {
        if (res.data.isSuppressed) {
          setSelectedParticipantSuppression(res.data);
        } else {
          setSelectedParticipantSuppression(null);
        }
      })
      .catch(() => setSelectedParticipantSuppression(null))
      .finally(() => setCheckingSuppression(false));
  }, [selectedParticipant, dataTable.evento_id]);

  const getParticipantName = (p) => {
    const fields = normalizeJsonObject(p?.json);
    for (const key of Object.keys(fields)) {
      const field = fields[key];
      const label = field && typeof field === "object" && !Array.isArray(field)
        ? field.label
        : key;
      const value = getJsonFieldValue(field);
      const lbl = String(label || "").toLowerCase();
      if ((lbl.includes("nome") || lbl.includes("name")) && value) return value;
    }
    return "";
  };

  const participantOptions = participants.map((p) => {
    const participantName = getParticipantName(p);
    const userType = USER_TYPE_OPTIONS.find((option) => String(option.value) === String(p.type));
    return {
      value: p.user_id,
      label: `${p.email}${participantName ? ` — ${participantName}` : ""}`,
      email: p.email,
      type: p.type,
      typeLabel: userType?.label || "Tipo não identificado",
    };
  });

  const templateOptions = (dataTemplates || []).map((t) => ({
    value: t.template_id || t.id,
    label: t.name || t.nome,
    version: t.template_version,
    subject: t.subject,
    qrcode: t.qrcode,
    rsvp_active: t.rsvp_active,
    has_rsvp: templateHasRsvp(t),
    has_html: !!(t.html && t.html.trim().length >= 50),
  }));

  const allEmails = dataEmail || [];

  const participantEmails = selectedParticipant
    ? allEmails.filter((e) => String(e.user_id) === String(selectedParticipant.value))
    : [];

  // Same template, different version — pick the highest version sent
  const sameTemplateSend = selectedTemplate
    ? participantEmails
      .filter(
        (e) =>
          e.template_version != null &&
          String(e.template_id) === String(selectedTemplate.value) &&
          Number(e.template_version).toFixed(1) !== Number(selectedTemplate.version).toFixed(1)
      )
      .reduce(
        (max, e) =>
          max === null || Number(e.template_version) > Number(max.template_version) ? e : max,
        null
      )
    : null;

  // Exact duplicate (same template AND same version)
  const exactSend = selectedTemplate
    ? participantEmails.find(
      (e) =>
        e.template_version != null &&
        String(e.template_id) === String(selectedTemplate.value) &&
        Number(e.template_version).toFixed(1) === Number(selectedTemplate.version).toFixed(1)
    ) ?? null
    : null;

  const canSend =
    !!selectedParticipant &&
    !!selectedTemplate &&
    selectedTemplate.has_html !== false &&
    !submitting &&
    !checkingSuppression &&
    !selectedParticipantSuppression;

  const handleSend = async () => {
    if (!canSend) return;
    if (selectedTemplate.has_rsvp && String(selectedParticipant.type) !== "0") {
      notyf.open({
        type: "warning",
        message: "Este modelo contém RSVP e só pode ser enviado a participantes (tipo 0). Selecione um participante para continuar.",
        duration: 7000,
        dismissible: true,
      });
      return;
    }

    setSubmitting(true);
    try {
      await axios.post("/api/event/email-individual", {
        event_id: dataTable.evento_id,
        user_id: selectedParticipant.value,
        template_id: selectedTemplate.value,
        template_version: selectedTemplate.version,
        creation_date: formatInTimeZone(new Date(), "UTC", "yyyy-MM-dd HH:mm"),
      });
      onSave && onSave();
    } catch (err) {
      notyf.error(err?.response?.data?.message || "Erro ao criar e-mail.");
      if (err?.response?.data?.code === "TEMPLATE_VERSION_STALE") onTemplateStale?.();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} fullscreen backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Enviar e-mail individual</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="py-5 d-flex justify-content-center">
            <Loader />
          </div>
        ) : (
          <>
            {/* 1. Template */}
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold d-flex align-items-center">
                <Badge bg="primary" pill className="me-2">
                  1
                </Badge>
                Modelo de e-mail
              </Form.Label>
              <Select
                options={templateOptions}
                value={selectedTemplate}
                onChange={setSelectedTemplate}
                placeholder="Clique para selecionar um modelo..."
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                formatOptionLabel={(opt) => <span>{opt.label}</span>}
                styles={{
                  control: (provided) => ({ ...provided, padding: 4 }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
              {selectedTemplate && selectedTemplate.has_html === false && (
                <Alert variant="danger" className="p-2 d-flex align-items-center">
                  <FontAwesomeIcon icon={faExclamationCircle} className="me-2 flex-shrink-0" />
                  <span>Este modelo não possui HTML configurado.</span>
                </Alert>
              )}
              {selectedParticipant && sameTemplateSend && !exactSend && (
                <Alert variant="primary" className="p-2 d-flex align-items-center">
                  <FontAwesomeIcon icon={faExclamationCircle} className="me-2 flex-shrink-0" />
                  <span>
                    Este modelo já foi enviado para este destinatário (v
                    {Number(sameTemplateSend.template_version).toFixed(1)}), mas com uma versão
                    diferente da atual (v{Number(selectedTemplate.version).toFixed(1)}).{" "}
                    Verifique se deseja enviar com a versão atualizada.
                  </span>
                </Alert>
              )}
              {selectedParticipant && exactSend && (
                <Alert variant="danger" className="p-2 d-flex align-items-center">
                  <FontAwesomeIcon icon={faExclamationCircle} className="me-2 flex-shrink-0" />
                  <span>
                    <strong>Réplica identificada:</strong> Este modelo na versão{" "}
                    <strong>v{Number(selectedTemplate.version).toFixed(1)}</strong> já foi enviado
                    para este destinatário (ID {exactSend.id} —{" "}
                    {EMAIL_STATUS_CONFIG[exactSend.status]?.label || exactSend.status}). Enviar
                    resultará em um envio idêntico.
                  </span>
                </Alert>
              )}
            </Form.Group>

            {/* 2. Recipient */}
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold d-flex align-items-center">
                <Badge bg="primary" pill className="me-2">
                  2
                </Badge>
                Destinatário
              </Form.Label>
              <Select
                options={participantOptions}
                value={selectedParticipant}
                onChange={setSelectedParticipant}
                formatOptionLabel={(option, { context }) => context === "menu" ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 1, lineHeight: 1.1 }}>
                    <span>{option.label}</span>
                    <small style={{ color: "inherit", fontSize: 11, lineHeight: 1.1 }}>{option.typeLabel}</small>
                  </div>
                ) : option.label}
                placeholder="Buscar por e-mail ou nome..."
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  control: (provided) => ({ ...provided, padding: 4 }),
                  option: (provided) => ({ ...provided, padding: "8px 12px" }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
              {selectedParticipantSuppression && (
                <Alert variant="danger" className="p-2 d-flex align-items-center">
                  <FontAwesomeIcon icon={faBan} className="me-2 flex-shrink-0" />
                  <span>
                    Este e-mail está na <strong>lista de supressão</strong>{" "}
                    (motivo:{" "}
                    <strong>
                      {SUPPRESSION_REASON_CONFIG[selectedParticipantSuppression.reason]?.label ||
                        selectedParticipantSuppression.reason}
                    </strong>
                    ). Remova-o das supressões antes de enviar.
                  </span>
                </Alert>
              )}
            </Form.Group>

            {/* 3. Summary */}
            {canSend && (
              <div className="rounded border overflow-hidden">
                <div
                  className="px-3 py-2 fw-semibold border-bottom d-flex align-items-center gap-2"
                  style={{ backgroundColor: "#f8f9fa", fontSize: 13 }}
                >
                  <Badge bg="primary" pill className="me-1">
                    3
                  </Badge>
                  Resumo do envio
                </div>
                <div className="px-3 py-3">
                  <table
                    style={{
                      width: "100%",
                      fontSize: 13,
                      borderCollapse: "separate",
                      borderSpacing: "0 6px",
                    }}
                  >
                    <tbody>
                      <tr>
                        <td
                          className="text-muted pe-3"
                          style={{ width: 130, verticalAlign: "top" }}
                        >
                          Modelo
                        </td>
                        <td className="fw-semibold">
                          {selectedTemplate.label}
                          <span
                            className="badge bg-primary text-dark ms-2"
                            style={{ fontSize: 10 }}
                          >
                            v.{Number(selectedTemplate.version || 0).toFixed(1)}
                          </span>
                          {selectedTemplate.qrcode ? (
                            <span
                              className="badge bg-primary text-dark ms-2"
                              style={{ fontSize: 10 }}
                            >
                              Código QR
                            </span>
                          ) : null}
                          {selectedTemplate.rsvp_active ? (
                            <span
                              className="badge bg-primary text-dark ms-2"
                              style={{ fontSize: 10 }}
                            >
                              RSVP Ativo
                            </span>
                          ) : null}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted pe-3" style={{ verticalAlign: "top" }}>
                          Assunto
                        </td>
                        <td>
                          {selectedTemplate.subject ? (
                            <span>{selectedTemplate.subject}</span>
                          ) : (
                            <span className="text-danger">
                              <FontAwesomeIcon icon={faExclamationCircle} className="me-1" />
                              Assunto não definido — edite o modelo de e-mail antes de enviar
                            </span>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted pe-3" style={{ verticalAlign: "top" }}>
                          Destinatário
                        </td>
                        <td>
                          <span className="fw-semibold">1</span> destinatário
                          <span className="text-muted ms-2" style={{ fontSize: 12 }}>
                            ({selectedParticipant.email})
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleSend} disabled={!canSend}>
          <FontAwesomeIcon icon={faEnvelope} className="me-2" />
          {submitting ? "Enviando..." : "Enviar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ─── EmailIndividualSection ─────────────────────────────────────────────────────
const EmailIndividualSection = ({ dataTable, dataEmail, dataBatchEmail, dataTemplates, dataSuppressions, setDataTableUpdate, resetTrigger, setResetTrigger }) => {
  const notyf = useContext(NotyfContext);
  const { setLoading } = useLayout();
  const userState = useAuth();
  const [triggerExport, setTriggerExport] = useState(false);
  const [resendEmailItem, setResendEmailItem] = useState(null);
  const [detailsEmailItem, setDetailsEmailItem] = useState(null);
  const [providerEvents, setProviderEvents] = useState([]);
  const [providerEventsLoading, setProviderEventsLoading] = useState(false);

  const [showSendIndividual, setShowSendIndividual] = useState(false);
  const [userInfoModal, setUserInfoModal] = useState(false);
  const [userInfoData, setUserInfoData] = useState(null);
  const [userInfoId, setUserInfoId] = useState(null);
  const [userInfoLoading, setUserInfoLoading] = useState(false);

  const allEmails = useMemo(() => dataEmail || [], [dataEmail]);
  const individualEmailCount = useMemo(
    () => allEmails.filter((email) =>
      email.batch_id === null ||
      email.batch_id === undefined ||
      Number(email.batch_id) === 0
    ).length,
    [allEmails],
  );
  const emailsRef = useRef(allEmails);

  // Use dataTable.platform directly — this is the authoritative source (comes from the backend
  // event record). Using userState.plataforma_eventos can be wrong if the admin belongs to a
  // different platform tier than the event being viewed.
  const platform = dataTable.platform || 2;

  useEffect(() => {
    emailsRef.current = allEmails;
  }, [allEmails]);

  useEffect(() => {
    if (!detailsEmailItem?.id) {
      setProviderEvents([]);
      return undefined;
    }
    let active = true;
    setProviderEventsLoading(true);
    axios.get(`/api/event/emails/${detailsEmailItem.id}/provider-events?event_id=${dataTable.evento_id}`)
      .then((response) => {
        if (active) setProviderEvents(response.data?.events || []);
      })
      .catch((error) => {
        console.error("Error fetching email provider events:", error);
        if (active) setProviderEvents([]);
      })
      .finally(() => {
        if (active) setProviderEventsLoading(false);
      });
    return () => { active = false; };
  }, [dataTable.evento_id, detailsEmailItem?.id]);

  const [filter, setFilter] = useState({
    dateRange: getDefaultDateRangeMonth(),
    statusLabel: "all",
    extraPropsEmails: false,
  });
  const [refreshTriggerLocal, setRefreshTriggerLocal] = useState(0);

  useEffect(() => {
    if (refreshTriggerLocal > 0) {
      setLoading(true);
      const timer = setTimeout(() => setLoading(false), 400);
      return () => clearTimeout(timer);
    }
  }, [refreshTriggerLocal, setLoading]);

  const getUserType = (type) => {
    switch (type) {
      case 0:
        return "Comum";
      case 1:
        return "Gestor";
      case 2:
        return "Expositor";
      default:
        return "N/A";
    }
  };

  const handleOpenUserInfo = useCallback((id) => {
    if (!id) return;
    setUserInfoId(id);
    setUserInfoModal(true);
    setUserInfoLoading(true);
    setUserInfoData(null); // will be populated after fetch

    // Fetch user info with a 500ms delay as requested
    setTimeout(() => {
      axios
        .get(`/api/event/participants/${dataTable.evento_id}?id=${id}&platform=${platform}`)
        .then((response) => {
          const participants = response.data.participants || [];
          if (participants.length > 0) {
            setUserInfoData(participants[0]);
          }
        })
        .catch((error) => {
          console.error("Error fetching user info:", error);
        })
        .finally(() => {
          setUserInfoLoading(false);
        });
    }, 500);
  }, [dataTable.evento_id, platform]);

  const handleDateRangeChange = (field, value) => {
    setFilter((prev) => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        [field]: value,
      },
    }));
  };

  const shouldShowUnreadCircle = useCallback((emailRecord) => {
    return emailRecord?.viewed === false;
  }, []);

  const handlePageViewed = useCallback((pageRows) => {
    const unreadIds = pageRows
      .filter(row => !row.viewed && row.id)
      .map(row => row.id);
    if (unreadIds.length === 0) return;

    const emails = emailsRef.current;
    emails.forEach(e => {
      if (unreadIds.includes(e.id)) e.viewed = true;
    });

    axios
      .put("/api/event/emails/viewed", { ids: unreadIds, event_id: dataTable.evento_id })
      .catch(e => console.log(e));
  }, [dataTable.evento_id]);

  const filteredEmailsForTable = useMemo(() => {
    return allEmails.filter((em) => {
      // Filter by Status
      if (filter.statusLabel !== "all" && String(em.status) !== String(filter.statusLabel)) {
        return false;
      }

      // Filter by Date — use send_date (real column name)
      const dateField = em.send_date || em.creation_date;
      if (!dateField) return true;
      const sentDate = new Date(dateField);
      if (!isValid(sentDate)) return true;
      if (filter.dateRange.startDate && filter.dateRange.endDate) {
        return sentDate >= filter.dateRange.startDate && sentDate <= filter.dateRange.endDate;
      } else if (filter.dateRange.startDate) {
        return sentDate >= filter.dateRange.startDate;
      } else if (filter.dateRange.endDate) {
        return sentDate <= filter.dateRange.endDate;
      }
      return true;
    });
  }, [refreshTriggerLocal, allEmails]);

  const handleDeleteEmail = useCallback((emailRecord) => {
    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => (
        <ShowConfirm
          title="Confirmar ação"
          description="Deseja excluir este e-mail permanentemente? Esta ação não pode ser desfeita."
          action={async () => {
            onClose();
            setLoading(true);
            try {
              await axios.delete(`/api/event/email/${emailRecord.id}?event_id=${dataTable.evento_id}`);
              notyf.success("E-mail excluído com sucesso.");
              setDataTableUpdate((prev) => prev + 1);
            } catch (err) {
              notyf.error(err?.response?.data?.message || "Erro ao excluir e-mail.");
            } finally {
              setLoading(false);
            }
          }}
          onClose={onClose}
        />
      ),
    });
  }, [dataTable.evento_id, notyf, setDataTableUpdate, setLoading]);

  const handleDeleteAllIndividualEmails = useCallback(() => {
    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => (
        <ShowConfirm
          title="Confirmar ação"
          description="Deseja excluir todos os e-mails individuais deste evento? Os e-mails enviados por lotes e os próprios lotes serão preservados."
          action={async () => {
            onClose();
            setLoading(true);
            try {
              await axios.delete(`/api/event/emails?event_id=${dataTable.evento_id}`);
              notyf.success("Todos os e-mails individuais foram excluídos.");
              setDataTableUpdate((prev) => prev + 1);
            } catch (err) {
              notyf.error(err?.response?.data?.message || "Erro ao excluir os e-mails individuais.");
            } finally {
              setLoading(false);
            }
          }}
          onClose={onClose}
        />
      ),
    });
  }, [dataTable.evento_id, notyf, setDataTableUpdate, setLoading]);

  const handleAddSuppression = useCallback((emailRecord) => {
    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => (
        <ShowConfirm
          title="Confirmar ação"
          description={`Deseja adicionar o e-mail "${emailRecord.email}" à lista de supressões? Este endereço não receberá mais e-mails futuros associados a este evento.`}
          action={async () => {
            onClose();
            setLoading(true);
            try {
              await axios.post(`/api/event/email-suppression`, {
                email: emailRecord.email,
                reason: "manual",
                event_id: dataTable.evento_id
              });
              notyf.success("E-mail adicionado à lista de supressão com sucesso.");
              setDataTableUpdate((prev) => prev + 1);
            } catch (err) {
              notyf.error(err?.response?.data?.message || "Erro ao adicionar à lista de supressão.");
            } finally {
              setLoading(false);
            }
          }}
          onClose={onClose}
        />
      ),
    });
  }, [dataTable.evento_id, notyf, setDataTableUpdate, setLoading]);

  const columns = useMemo(
    () => [
      {
        Header: "E-mail",
        accessor: "email",
        Cell: ({ row, value }) => (
          <span className="d-inline-flex align-items-center gap-2">
            {shouldShowUnreadCircle(row.original) && (
              <span
                className="bg-primary rounded-circle d-inline-block"
                style={{ width: '10px', height: '10px', flexShrink: 0 }}
                title="E-mail ainda não visualizado"
              ></span>
            )}
            <span>{value || <span className="opacity-50">(Não preenchido)</span>}</span>
          </span>
        ),
      },
      {
        Header: "Modelo de e-mail",
        accessor: "template_name",
        Filter: SelectColumnFilter,
        filter: "equals",
        Cell: ({ row }) => (
          <>
            {row.original.template_name || ""}
            {row.original.template_version != null && (
              <Badge bg="primary" className="ms-2 text-black" style={{ fontSize: 10 }}>
                v.{Number(row.original.template_version).toFixed(1)}
              </Badge>
            )}
          </>
        ),
      },
      {
        Header: "Origem",
        id: "type",
        accessor: (row) => EMAIL_TYPE_CONFIG[row.type] || row.type || "",
        Filter: SelectColumnFilter,
        filter: "equals",
        Cell: ({ row }) => {
          const typeLabel = EMAIL_TYPE_CONFIG[row.original.type] || row.original.type || <span className="opacity-50">(Não preenchido)</span>;
          const isBatch = row.original.type === "email_painel_managament_batch";
          return (
            <span>
              {typeLabel}
              {isBatch && row.original.batch_id && (
                <>
                  <br />
                  <small className="text-muted">
                    {row.original.batch_name ? `${row.original.batch_name} (#${row.original.batch_id})` : `#${row.original.batch_id}`}
                  </small>
                </>
              )}
            </span>
          );
        },
      },
      {
        Header: "Status",
        id: "status",
        accessor: (row) => EMAIL_STATUS_CONFIG[row.status]?.label || row.status,
        Filter: SelectColumnFilter,
        filter: "equals",
        Cell: ({ row }) => {
          const cfg = EMAIL_STATUS_CONFIG[row.original.status] || { bg: "secondary", label: row.original.status };
          return <Badge bg={cfg.bg}>{cfg.label}</Badge>;
        },
      },
      { Header: "Criado em", accessor: "creation_date_display" },
      { Header: "Enviado em", accessor: "send_date_display" },
      ...(filter.extraPropsEmails
        ? [
          {
            Header: "User ID",
            accessor: "user_id",
            Cell: ({ value }) => value ? <span>{value}</span> : "",
          },
          {
            Header: "SES ID",
            accessor: "ses_message_id",
            Cell: ({ value }) => value ? (
              <OverlayTrigger overlay={<Tooltip>{value}</Tooltip>}>
                <span className="text-muted" style={{ cursor: "pointer", fontSize: 13 }}>
                  {value.length > 8 ? value.substring(0, 8) + "..." : value}
                </span>
              </OverlayTrigger>
            ) : "",
          },
          {
            Header: "Erro",
            accessor: "error",
            Cell: ({ value }) => value ? (
              <OverlayTrigger overlay={<Tooltip>{value}</Tooltip>}>
                <span className="text-danger" style={{ cursor: "pointer", fontSize: 13 }}>
                  {value.length > 30 ? value.substring(0, 30) + "..." : value}
                </span>
              </OverlayTrigger>
            ) : "",
          },
        ]
        : []),
      {
        Header: "",
        id: "icons",
        disableSortBy: true,
        disableFilters: true,
        Cell: ({ row }) => (
          <>
            {!row.original.batch_id && (
              <>
                <OverlayTrigger overlay={<Tooltip>Excluir e-mail</Tooltip>}>
                  <span className="d-inline-block mx-1">
                    <Trash
                      role="button"
                      className="align-middle cursor-pointer"
                      size={18}
                      onClick={() => handleDeleteEmail(row.original)}
                    />
                  </span>
                </OverlayTrigger>
                <OverlayTrigger overlay={<Tooltip>Reenviar e-mail</Tooltip>}>
                  <span className="d-inline-block mx-1">
                    <Send
                      role="button"
                      className="align-middle cursor-pointer"
                      size={18}
                      onClick={() => setResendEmailItem(row.original)}
                    />
                  </span>
                </OverlayTrigger>
              </>
            )}
            {(row.original.status === "bounced" || row.original.status === "complained") && (
              <OverlayTrigger overlay={<Tooltip>Adicionar à lista de supressão</Tooltip>}>
                <span className="d-inline-block mx-1">
                  <FontAwesomeIcon
                    icon={faBan}
                    role="button"
                    className="align-middle cursor-pointer"
                    style={{ fontSize: 16 }}
                    onClick={() => handleAddSuppression(row.original)}
                  />
                </span>
              </OverlayTrigger>
            )}
            {row.original.user_id && (
              <OverlayTrigger overlay={<Tooltip>Ver dados do participante</Tooltip>}>
                <span className="d-inline-block mx-1">
                  <User
                    role="button"
                    className="align-middle cursor-pointer"
                    size={18}
                    onClick={() => handleOpenUserInfo(row.original.user_id)}
                  />
                </span>
              </OverlayTrigger>
            )}
            <OverlayTrigger overlay={<Tooltip>Ver detalhes do e-mail</Tooltip>}>
              <span className="d-inline-block mx-1">
                <Mail
                  role="button"
                  className="align-middle cursor-pointer"
                  size={18}
                  onClick={() => setDetailsEmailItem(row.original)}
                />
              </span>
            </OverlayTrigger>
          </>
        ),
      },
    ],
    [filter.extraPropsEmails, handleDeleteEmail, handleAddSuppression, shouldShowUnreadCircle, handleOpenUserInfo],
  );

  return (
    <>
      <Form.Group
        as={Col}
        className="textHeaderAplicativo d-flex justify-content-between align-items-center"
      >
        <h5 className="card-title mb-0">Todos os e-mails ({allEmails.length})</h5>
        <div>
          <Dropdown className="d-inline">
            <Dropdown.Toggle className="btn btn-outline-primary">
              <FontAwesomeIcon icon={faCog} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setTriggerExport(true)}>Exportar</Dropdown.Item>
              <Dropdown.Item
                disabled={individualEmailCount === 0}
                onClick={handleDeleteAllIndividualEmails}
              >
                Excluir todos os e-mails individuais
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() =>
                  setFilter((prev) => ({
                    ...prev,
                    extraPropsEmails: !prev.extraPropsEmails,
                  }))
                }
              >
                {!filter.extraPropsEmails
                  ? "Mostrar mais informações"
                  : "Esconder informações extras"}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <Button
            type="button"
            className="btn btn-outline-primary ms-2"
            onClick={() => setShowSendIndividual(true)}
          >
            <FontAwesomeIcon icon={faPlus} />
          </Button>
        </div>
      </Form.Group>
      <h6 className="text-muted mt-3 mx-2">
        Consulte o status individual de cada e-mail disparado
      </h6>

      {/* Filters: Date Range + Batch */}
      <Row className="mb-3 mx-0 g-2">
        <Col xs={12} sm={6} md={3}>
          <InputGroup className="w-100 flex-nowrap">
            <div className="flex-fill">
              <Datetime
                className="w-100"
                value={
                  filter.dateRange.startDate
                    ? format(new Date(filter.dateRange.startDate), "dd/MM/yyyy HH:mm")
                    : ""
                }
                onChange={(date) => {
                  if (date && typeof date === "object" && date._isValid !== false) {
                    handleDateRangeChange("startDate", date.toDate());
                  } else {
                    handleDateRangeChange("startDate", typeof date === "string" ? date : null);
                  }
                }}
                dateFormat="DD/MM/YYYY"
                timeFormat="HH:mm"
                inputProps={{
                  placeholder: "Data Inicial",
                  className: "form-control",
                  style: { borderTopRightRadius: 0, borderBottomRightRadius: 0 }
                }}
                locale="pt-br"
              />
            </div>
            <InputGroup.Text
              className="cursor-pointer"
              onClick={() => setRefreshTriggerLocal(prev => prev + 1)}
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </InputGroup.Text>
          </InputGroup>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <InputGroup className="w-100 flex-nowrap">
            <div className="flex-fill">
              <Datetime
                className="w-100"
                value={
                  filter.dateRange.endDate
                    ? format(new Date(filter.dateRange.endDate), "dd/MM/yyyy HH:mm")
                    : ""
                }
                onChange={(date) => {
                  if (date && typeof date === "object" && date._isValid !== false) {
                    handleDateRangeChange("endDate", date.toDate());
                  } else {
                    handleDateRangeChange("endDate", typeof date === "string" ? date : null);
                  }
                }}
                dateFormat="DD/MM/YYYY"
                timeFormat="HH:mm"
                inputProps={{
                  placeholder: "Data Final",
                  className: "form-control",
                  style: { borderTopRightRadius: 0, borderBottomRightRadius: 0 }
                }}
                locale="pt-br"
              />
            </div>
            <InputGroup.Text
              className="cursor-pointer"
              onClick={() => setRefreshTriggerLocal(prev => prev + 1)}
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </InputGroup.Text>
          </InputGroup>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Form.Select
            className="form-control w-100"
            value={filter.statusLabel}
            onChange={(e) => {
              setFilter((prev) => ({ ...prev, statusLabel: e.target.value }));
              setRefreshTriggerLocal(prev => prev + 1);
            }}
          >
            <option value="all">Todos os status</option>
            {Object.entries(EMAIL_STATUS_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>
                {config.label}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col xs={12} sm={6} md={3} className="d-flex align-items-center my-3 mb-sm-0 mt-sm-auto">
          <button
            className="btn btn-primary w-100 w-sm-auto"
            onClick={() => {
              setFilter({
                statusLabel: "all",
                dateRange: getDefaultDateRangeMonth(),
                extraPropsEmails: false,
              });
              setRefreshTriggerLocal(prev => prev + 1);
              if (setResetTrigger) setResetTrigger(prev => prev + 1);
            }}
            title="Limpar filtros"
          >
            <FontAwesomeIcon icon={faEraser} />
          </button>
        </Col>
      </Row>

      <div className="pb-3">
        <ColumnSortingTable
          key={"columnEmails" + filteredEmailsForTable.length}
          columns={columns}
          title={dataTable.title + " - emails"}
          data={filteredEmailsForTable}
          onPageChange={handlePageViewed}
          getCellProps={(cellinfo) => ({
            className: cellinfo.column.id === "icons" ? "table-icons-fourth" : "",
          })}
          triggerExport={triggerExport}
          setTriggerExport={setTriggerExport}
          resetTrigger={resetTrigger}
        />
      </div>

      <Modal className="logUserModal" show={userInfoModal} onHide={() => setUserInfoModal(false)} size="md" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Informações do Usuário
            <br /><small className="text-muted"><b>ID:</b> {userInfoId}</small>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {userInfoLoading ? (
            <div className="py-5 text-center">
              <Loader />
            </div>
          ) : userInfoData ? (
            (() => {
              const jsonStr =
                typeof userInfoData === "string"
                  ? userInfoData
                  : userInfoData.json || "";
              const parsedInfo = safeJsonParse(jsonStr);

              const displayEmail =
                userInfoData.email ||
                userInfoData.user_email ||
                (parsedInfo.email && typeof parsedInfo.email === "string"
                  ? parsedInfo.email
                  : parsedInfo.email?.value);

              const formFields = dataTable?.json?.form?.fields || [];
              const renderedKeys = new Set(["email"]);
              const elements = [];

              formFields.forEach((field) => {
                const key = String(field.inputID);
                const item = parsedInfo[key];
                const label = field.label || item?.label || key;
                let value = "";
                if (item !== undefined && item !== null && item !== "") {
                  value = item?.value !== undefined
                    ? item.value
                    : typeof item === "object"
                      ? JSON.stringify(item)
                      : String(item);
                }
                elements.push(
                  <p key={key} className="mb-0">
                    <strong>{label}:</strong>{" "}
                    <span className="opacity-75">
                      {value !== "" && value !== null && value !== undefined ? (
                        value
                      ) : (
                        <span className="opacity-50">
                          (Não preenchido)
                        </span>
                      )}
                    </span>
                  </p>
                );
                renderedKeys.add(key);
              });

              Object.entries(parsedInfo).forEach(([key, item]) => {
                if (!renderedKeys.has(key)) {
                  const label = item?.label || key;
                  let value = "";
                  if (item !== undefined && item !== null && item !== "") {
                    value = item?.value !== undefined
                      ? item.value
                      : typeof item === "object"
                        ? JSON.stringify(item)
                        : String(item);
                  }
                  elements.push(
                    <p key={key} className="mb-0">
                      <strong>{label}:</strong>{" "}
                      <span className="opacity-75">
                        {value !== "" && value !== null && value !== undefined ? (
                          value
                        ) : (
                          <span className="opacity-50">
                            (Não preenchido)
                          </span>
                        )}
                      </span>
                    </p>
                  );
                }
              });

              return (
                <>
                  <p className="mb-0">
                    <strong>E-mail:</strong>{" "}
                    <span className="opacity-75">
                      {displayEmail || (
                        <span className="opacity-50">(Não preenchido)</span>
                      )}
                    </span>
                  </p>
                  <Row>{elements}</Row>

                  {platform === 2 &&
                    (userInfoId || userInfoData.user_id) && (
                      <UserAdditionalInfoSection
                        platform={2}
                        data={userInfoData}
                        convertDateToDisplay={convertDateToDisplay}
                        getUserType={getUserType}
                      />
                    )}

                  {platform === 1 &&
                    (userInfoId || userInfoData.visitante_id) && (
                      <UserAdditionalInfoSection
                        platform={1}
                        data={userInfoData}
                        convertDateToDisplay={convertDateToDisplay}
                      />
                    )}
                </>
              );
            })()
          ) : (
            <div className="text-center py-3 text-muted">Sem dados disponíveis</div>
          )}
        </Modal.Body>
      </Modal>

      {/* Resend modal */}
      <ResendEmailModal
        emailItem={resendEmailItem}
        onHide={(refresh) => {
          setResendEmailItem(null);
          if (refresh) setDataTableUpdate((prev) => prev + 1);
        }}
        dataTable={dataTable}
        dataTemplates={dataTemplates}
        dataEmail={allEmails}
        dataSuppressions={dataSuppressions}
      />

      {showSendIndividual && (
        <SendIndividualEmailModal
          show={showSendIndividual}
          onHide={() => setShowSendIndividual(false)}
          dataTable={dataTable}
          dataTemplates={dataTemplates}
          dataEmail={allEmails}
          onTemplateStale={() => {
            setShowSendIndividual(false);
            setDataTableUpdate((prev) => prev + 1);
          }}
          onSave={() => {
            setShowSendIndividual(false);
            setDataTableUpdate((prev) => prev + 1);
            notyf.success("E-mail adicionado à fila de envio.");
          }}
        />
      )}

      {/* Details Modal */}
      {detailsEmailItem && (
        <Modal show={true} onHide={() => setDetailsEmailItem(null)} size="lg" centered fullscreen="lg-down">
          <Modal.Header closeButton>
            <Modal.Title>
              Detalhes do E-mail
              <br />
              <small>
                <span className="opacity-75">
                  ID: {detailsEmailItem.id}
                </span>
              </small>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: 600, overflowY: "auto" }}>
            <div className="p-3 border rounded mb-4">
              <Row>
                <Col sm={6}>
                  <p className="mb-1"><strong>Status:</strong> <Badge bg={EMAIL_STATUS_CONFIG[detailsEmailItem.status]?.bg || "secondary"}>{EMAIL_STATUS_CONFIG[detailsEmailItem.status]?.label || detailsEmailItem.status}</Badge></p>
                  <p className="mb-1"><strong>Destinatário:</strong> {detailsEmailItem.email || "N/A"}</p>
                  <p className="mb-1">
                    <strong>Origem:</strong>{" "}
                    {EMAIL_TYPE_CONFIG[detailsEmailItem.type] || detailsEmailItem.type || "N/A"}
                    {detailsEmailItem.type === "email_painel_managament_batch" && detailsEmailItem.batch_id && (
                      <>
                        {" — "}
                        <span className="text-muted">
                          {detailsEmailItem.batch_name ? `${detailsEmailItem.batch_name} (#${detailsEmailItem.batch_id})` : `#${detailsEmailItem.batch_id}`}
                        </span>
                      </>
                    )}
                  </p>
                  <p className="mb-1"><strong>Modelo:</strong> {detailsEmailItem.template_name || "N/A"}</p>
                  <p className="mb-1"><strong>Versão:</strong> {detailsEmailItem.template_version !== null ? `v.${Number(detailsEmailItem.template_version).toFixed(1)}` : "N/A"}</p>
                  <p className="mb-1"><strong>E-mail de resposta:</strong> {detailsEmailItem.template_reply_to || "N/A"}</p>
                </Col>
                <Col sm={6}>
                  <p className="mb-1"><strong>Criado em:</strong> {detailsEmailItem.creation_date_display || "N/A"}</p>
                  <p className="mb-1"><strong>Enviado em:</strong> {detailsEmailItem.send_date_display || "N/A"}</p>
                  <p className="mb-1"><strong>Entregue em:</strong> {detailsEmailItem.delivered_at ? convertDateToDisplay(detailsEmailItem.delivered_at) : "Ainda não confirmado"}</p>
                  <p className="mb-1"><strong>User ID:</strong> {detailsEmailItem.user_id || "N/A"}</p>
                </Col>
              </Row>
              {detailsEmailItem.delivery_outcome && (
                <>
                  <hr className="my-2" />
                  <p className="mb-1">
                    <strong>Status de entrega:</strong>{" "}
                    <Badge bg={PROVIDER_OUTCOME_CONFIG[detailsEmailItem.delivery_outcome]?.bg || "secondary"}>
                      {PROVIDER_OUTCOME_CONFIG[detailsEmailItem.delivery_outcome]?.label || detailsEmailItem.delivery_outcome}
                    </Badge>
                  </p>
                  {detailsEmailItem.provider_diagnostic && (
                    <p className="mb-0 text-break"><strong>Diagnóstico:</strong> {detailsEmailItem.provider_diagnostic}</p>
                  )}
                  {detailsEmailItem.delivery_outcome === "delivered_to_recipient_server" && (
                    <small className="d-block text-muted mt-2">O servidor de e-mail do destinatário aceitou esta mensagem. Não é possível confirmar a caixa de entrada, a pasta de spam ou a leitura.</small>
                  )}
                </>
              )}
              {(detailsEmailItem.ses_message_id) && (
                <>
                  <hr className="my-2" />
                  <Row>
                    <Col>
                      <p className="mb-0"><strong>SES ID:</strong></p>
                      <div className="p-2 border rounded bg-light mt-1 text-break" style={{ fontSize: 13 }}>
                        {detailsEmailItem.ses_message_id}
                      </div>
                    </Col>
                  </Row>
                </>
              )}
              {(detailsEmailItem.error) && (
                <>
                  <hr className="my-2" />
                  <Row>
                    <Col>
                      <p className="mb-0 text-danger"><strong>Erro:</strong></p>
                      <div className="p-2 border rounded bg-light mt-1 text-danger text-break" style={{ fontSize: 13 }}>
                        {detailsEmailItem.error}
                      </div>
                    </Col>
                  </Row>
                </>
              )}
            </div>
            <Row className="mb-3">
              <Col>
                <strong>Rastreio do provedor:</strong>
                <div className="p-2 border rounded bg-light mt-1">
                  {providerEventsLoading ? (
                    <span className="opacity-75">Carregando eventos do provedor…</span>
                  ) : providerEvents.length > 0 ? (
                    <ul className="list-unstyled mb-0">
                      {providerEvents.map((providerEvent) => {
                        const outcome = PROVIDER_OUTCOME_CONFIG[providerEvent.normalized_outcome] || { bg: "secondary", label: providerEvent.normalized_outcome };
                        const time = providerEvent.provider_event_at || providerEvent.created_at;
                        const diagnostics = [
                          providerEvent.smtp_status,
                          providerEvent.smtp_diagnostic,
                          providerEvent.bounce_type && `Tipo de devolução: ${providerEvent.bounce_type}`,
                          providerEvent.bounce_subtype && `Subtipo: ${providerEvent.bounce_subtype}`,
                          providerEvent.delay_type && `Motivo do atraso: ${providerEvent.delay_type}`,
                          providerEvent.recipient_server && /^(?:\d{1,3}\.){3}\d{1,3}$|^[0-9a-f:]+$/i.test(providerEvent.recipient_server) && `IP do servidor destinatário: ${providerEvent.recipient_server}`,
                        ].filter(Boolean);
                        return (
                          <li key={providerEvent.id} className="py-2 border-bottom last-child-border-0">
                            <div className="d-flex flex-wrap align-items-center gap-2">
                              <Badge bg={outcome.bg}>{outcome.label}</Badge>
                              <span className="small text-muted">{convertDateToDisplay(time)}</span>
                            </div>
                            <div className="small mt-1">{PROVIDER_EVENT_LABELS[providerEvent.event_type] || providerEvent.event_type}</div>
                            {diagnostics.length > 0 && <div className="small text-muted text-break mt-1">{diagnostics.join(" · ")}</div>}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <span className="opacity-75">Ainda não há evidências do provedor para este e-mail.</span>
                  )}
                </div>
              </Col>
            </Row>
            {/* Show variables if any */}
            {detailsEmailItem.variables && (
              <Row className="mb-3">
                <Col>
                  <strong>Variáveis:</strong>
                  <pre className="p-2 border rounded bg-light mt-1" style={{ whiteSpace: "pre-wrap", maxHeight: "200px", overflow: "auto" }}>
                    {typeof detailsEmailItem.variables === 'string'
                      ? detailsEmailItem.variables
                      : JSON.stringify(detailsEmailItem.variables, null, 2)}
                  </pre>
                </Col>
              </Row>
            )}
            {/* Show retries always */}
            <Row>
              <Col>
                <strong>Histórico de reentativas:</strong>
                <div className="p-2 border rounded bg-light mt-1">
                  {detailsEmailItem.retried_at_formatted && detailsEmailItem.retried_at_formatted.length > 0 ? (
                    <ul className="mb-0">
                      {detailsEmailItem.retried_at_formatted.map((rt, i) => (
                        <li key={i}>{rt}</li>
                      ))}
                    </ul>
                  ) : (
                    <span className="py-3 opacity-75">(Nenhum reenvio foi realizado)</span>
                  )}
                </div>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setDetailsEmailItem(null)}>
              Fechar
            </Button>
            {!detailsEmailItem.batch_id && (
              <Button
                variant="primary"
                onClick={() => {
                  setResendEmailItem(detailsEmailItem);
                  setDetailsEmailItem(null);
                }}
              >
                Reenviar E-mail
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

// ─── EmailTemplateSection ─────────────────────────────────────────────────────
const EmailTemplateSection = ({ dataTable, dataTemplates, setDataTableUpdate, onRegisterTemplateVersionChange }) => {
  const notyf = useContext(NotyfContext);

  // Use the parent-managed data — no local fetch needed
  const templates = dataTemplates || [];

  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [triggerExport, setTriggerExport] = useState(false);

  const handleAdd = () => {
    setEditingTemplate(null);
    setShowEditor(true);
  };

  const handleEdit = (tpl) => {
    setEditingTemplate(tpl);
    setShowEditor(true);
  };

  const deleteItem = (templateId) => {
    axios
      .delete(`/api/event/email-templates/${templateId}`)
      .then(function () {
        notyf.success("Modelo de e-mail excluído com sucesso.");
        if (setDataTableUpdate) setDataTableUpdate((prev) => prev + 1);
      })
      .catch(function (error) {
        console.error("Error deleting template:", error);
        notyf.error(error?.response?.data?.message || "Erro ao excluir o modelo de e-mail.");
      });
  };

  const handleDelete = (templateId) => {
    const isUsedInRegister = isRegisterTemplateUsed(
      dataTable?.json?.email?.register,
      templateId,
    );

    if (isUsedInRegister) {
      notyf.error("Esse modelo de e-mail está definido para um tipo de usuário e não pode ser excluído.");
      return;
    }

    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => (
        <ShowConfirm
          title="Confirmar ação"
          description="Deseja remover esse modelo de e-mail?"
          action={() => {
            deleteItem(templateId);
            onClose();
          }}
          onClose={onClose}
        />
      ),
    });
  };

  const handleSave = async (tpl) => {
    const isUsedInRegister = dataTable?.json?.email?.register?.status
      && isRegisterTemplateUsed(dataTable?.json?.email?.register, tpl.template_id);

    const performSave = async () => {
      try {
        if (tpl.template_id) {
          const response = await axios.put(`/api/event/email-templates/${tpl.template_id}`, {
            ...tpl,
            expected_template_version: tpl.template_version,
          });
          const nextTemplateVersion = Number(response?.data?.template_version);

          if (isUsedInRegister && !Number.isNaN(nextTemplateVersion) && onRegisterTemplateVersionChange) {
            onRegisterTemplateVersionChange({
              templateId: tpl.template_id,
              templateVersion: nextTemplateVersion,
            });
          }

          notyf.success("Modelo de e-mail atualizado com sucesso!");
        } else {
          await axios.post(`/api/event/email-templates`, tpl);
          notyf.success("Modelo de e-mail criado com sucesso!");
        }
        setShowEditor(false);
        // Parent useEffect refetches all data (templates + batches + emails) in one shot
        if (setDataTableUpdate) setDataTableUpdate((prev) => prev + 1);
      } catch (error) {
        console.error("Error saving template:", error);
        notyf.error(error?.response?.data?.message || "Erro ao salvar modelo de e-mail.");
        if (error?.response?.data?.code === "TEMPLATE_VERSION_STALE" && setDataTableUpdate) setDataTableUpdate((prev) => prev + 1);
      }
    };

    if (tpl.template_id && isUsedInRegister) {
      confirmAlert({
        closeOnEscape: false,
        closeOnClickOutside: false,
        customUI: ({ onClose }) => (
          <ShowConfirm
            title="Confirmar ação"
            description="Esse modelo de e-mail está sendo usado para o envio de e-mail automático após registro, que será atualizado automaticamente para esse novo modelo. Deseja continuar?"
            action={() => {
              performSave();
              onClose();
            }}
            onClose={onClose}
          />
        ),
      });
    } else {
      performSave();
    }
  };

  return (
    <>
      {/* Header — same pattern as "Lista de registrados" */}
      <Form.Group
        as={Col}
        className="textHeaderAplicativo d-flex justify-content-between align-items-center"
      >
        <h5 className="card-title mb-0">
          Modelos de E-mail ({templates.length})
        </h5>
        <div>
          <Dropdown className="d-inline">
            <Dropdown.Toggle className="btn btn-outline-primary">
              <FontAwesomeIcon icon={faCog} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() => {
                  setTriggerExport(true);
                }}
              >
                Exportar
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <Button
            type="button"
            className="btn btn-outline-primary ms-2"
            onClick={handleAdd}
          >
            <FontAwesomeIcon icon={faPlus} />
          </Button>
        </div>
      </Form.Group>
      <h6 className="text-muted mt-3 mx-2">
        Gerencie os modelos de e-mail usados nos envios em lote
      </h6>

      <div className="pb-3 mb-3">
        {templates.length > 0 ? (
          <>
            <div className="d-flex justify-content-end mb-2 mt-2">
              <small className="text-muted">
                Mostrando {templates.length} de {templates.length} items
              </small>
            </div>
            <div className="list-group">
              {templates.map((value, index) => {
                const defaultTypes = getRegistrationTemplateUserTypes(dataTable?.json?.email?.register, value.template_id);
                const isDefault = isRegisterTemplateUsed(dataTable?.json?.email?.register, value.template_id);
                return (
                  <div
                    key={"templateItem" + index}
                    className="list-group-item list-group-item-action mb-2"
                    aria-current="true"
                  >
                    <div className="d-flex w-100 justify-content-between">
                      <h5 className="mb-1 me-2">
                        {value.name}
                      </h5>
                      {value.creation_date && <small>Criado em: {convertDateToDisplay(value.creation_date)}</small>}
                    </div>

                    <span bg={"primary"} className="badge bg-primary text-dark me-2">
                      v.{Number(value.template_version || 0).toFixed(1)}
                    </span>
                    {value.qrcode === 1 && <span bg={"primary"} className="badge bg-primary text-dark me-2">
                      Código QR
                    </span>}
                    {value.rsvp_active === 1 && (
                      <span bg={"primary"} className="badge bg-primary text-dark me-2">
                        RSVP Ativo
                      </span>
                    )}
                    {defaultTypes.map(({ type, label }) => <Badge key={type} bg="secondary" className="me-2">Padrão · {label}</Badge>)}
                    <OverlayTrigger overlay={<Tooltip>Editar modelo</Tooltip>}>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary float-end me-0"
                        onClick={() => handleEdit(value)}
                      >
                        <FontAwesomeIcon icon={faPencil} size="1x" />
                      </button>
                    </OverlayTrigger>
                    <OverlayTrigger overlay={<Tooltip>{isDefault ? "Selecione outro padrão para os tipos vinculados antes de excluir este modelo." : "Excluir modelo"}</Tooltip>}>
                      <span className="float-end me-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={isDefault}
                        aria-label={isDefault ? "Modelo padrão protegido contra exclusão" : "Excluir modelo"}
                        onClick={() => handleDelete(value.template_id)}
                      >
                        <FontAwesomeIcon icon={faTrash} size="1x" />
                      </button>
                      </span>
                    </OverlayTrigger>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <h5 className="py-5 text-center opacity-50">
            Nenhum modelo de e-mail encontrado
          </h5>
        )}
      </div>

      <EmailTemplateEditorModal
        show={showEditor}
        onHide={() => setShowEditor(false)}
        template={editingTemplate}
        sourceTemplates={templates}
        onSave={handleSave}
        onRsvpChanged={() => setDataTableUpdate?.((previous) => previous + 1)}
        dataTable={dataTable}
      />
    </>
  );
};

const EmailSuppressionsSection = ({ dataTable, dataSuppressions, setDataTableUpdate }) => {
  const notyf = useContext(NotyfContext);
  const { setLoading } = useLayout();
  const [triggerExport, setTriggerExport] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  const suppressions = dataSuppressions || [];

  // State for the manual add modal
  const [addEmail, setAddEmail] = useState("");
  const [addReason, setAddReason] = useState("manual");
  const [addSubmitting, setAddSubmitting] = useState(false);

  const handleAddManual = async (e) => {
    e.preventDefault();
    const emailTrimmed = addEmail.trim();
    if (!emailTrimmed) {
      notyf.error("Insira um endereço de e-mail.");
      return;
    }
    setAddSubmitting(true);
    try {
      await axios.post("/api/event/email-suppression", {
        email: emailTrimmed,
        reason: addReason,
        event_id: dataTable.evento_id,
      });
      notyf.success("E-mail adicionado à lista de supressão.");
      setAddEmail("");
      setAddReason("manual");
      setShowAddModal(false);
      if (setDataTableUpdate) setDataTableUpdate((prev) => prev + 1);
    } catch (err) {
      notyf.error(err?.response?.data?.message || "Erro ao adicionar supressão.");
    } finally {
      setAddSubmitting(false);
    }
  };

  const deleteSuppressionItem = useCallback((id) => {
    setLoading(true);
    axios
      .delete(`/api/event/email-suppressions/${id}?event_id=${dataTable.evento_id}`)
      .then(() => {
        notyf.success("Supressão removida com sucesso.");
        if (setDataTableUpdate) setDataTableUpdate((prev) => prev + 1);
      })
      .catch((err) => {
        notyf.error(err?.response?.data?.message || "Erro ao remover supressão.");
      })
      .finally(() => setLoading(false));
  }, [dataTable.evento_id, notyf, setDataTableUpdate, setLoading]);

  const handleDeleteSuppression = useCallback((id) => {
    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => (
        <ShowConfirm
          title="Confirmar ação"
          description="Deseja remover esta supressão? O endereço voltará a receber e-mails."
          action={() => {
            deleteSuppressionItem(id);
            onClose();
          }}
          onClose={onClose}
        />
      ),
    });
  }, [deleteSuppressionItem]);

  const handleDeleteAllSuppressions = useCallback(() => {
    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => (
        <ShowConfirm
          title="Confirmar ação"
          description="Deseja remover toda a blacklist deste evento? Estes endereços voltarão a receber e-mails deste evento."
          action={async () => {
            onClose();
            setLoading(true);
            try {
              await axios.delete(`/api/event/email-suppressions?event_id=${dataTable.evento_id}`);
              notyf.success("Toda a blacklist do evento foi removida.");
              if (setDataTableUpdate) setDataTableUpdate((prev) => prev + 1);
            } catch (err) {
              notyf.error(err?.response?.data?.message || "Erro ao remover a blacklist.");
            } finally {
              setLoading(false);
            }
          }}
          onClose={onClose}
        />
      ),
    });
  }, [dataTable.evento_id, notyf, setDataTableUpdate, setLoading]);

  const columns = useMemo(
    () => [
      { Header: "E-mail", accessor: "email" },
      {
        Header: "Motivo",
        accessor: "reason",
        Filter: SelectColumnFilter,
        filter: "equals",
        Cell: ({ value }) => {
          const cfg = SUPPRESSION_REASON_CONFIG[value] || { bg: "secondary", label: value };
          return <Badge bg={cfg.bg}>{cfg.label}</Badge>;
        },
      },
      {
        Header: "Escopo",
        id: "scope",
        accessor: (row) => (row.event_id ? `Evento #${row.event_id}` : "Global"),
        Filter: SelectColumnFilter,
        filter: "equals",
        Cell: ({ row }) => (
          <span className="text-muted" style={{ fontSize: 13 }}>
            {row.original.event_id ? `Evento #${row.original.event_id}` : "Global"}
          </span>
        ),
      },
      { Header: "Adicionado em", accessor: "created_at_display" },
      {
        Header: "",
        id: "icons",
        disableSortBy: true,
        disableFilters: true,
        Cell: ({ row }) => (
          <OverlayTrigger overlay={<Tooltip>Remover supressão</Tooltip>}>
            <span className="d-inline-block mx-1">
              <FontAwesomeIcon
                icon={faUndo}
                role="button"
                className="align-middle cursor-pointer"
                style={{ fontSize: 16 }}
                onClick={() => handleDeleteSuppression(row.original.id)}
              />
            </span>
          </OverlayTrigger>
        ),
      },
    ],
    [handleDeleteSuppression],
  );

  return (
    <>
      <Form.Group
        as={Col}
        className="textHeaderAplicativo d-flex justify-content-between align-items-center"
      >
        <h5 className="card-title mb-0">
          Blacklist ({suppressions.length})  <OverlayTrigger
            overlay={<Tooltip>A lista de supressão é compartilhada entre todos os eventos</Tooltip>}
          >
            <HelpTooltip icon={faExclamationCircle} />
          </OverlayTrigger>
        </h5>
        <div>
          <Dropdown className="d-inline">
            <Dropdown.Toggle className="btn btn-outline-primary">
              <FontAwesomeIcon icon={faCog} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setTriggerExport(true)}>Exportar</Dropdown.Item>
              <Dropdown.Item
                disabled={suppressions.length === 0}
                onClick={handleDeleteAllSuppressions}
              >
                Excluir blacklist
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <Button
            type="button"
            className="btn btn-outline-primary ms-2"
            onClick={() => {
              setAddEmail("");
              setAddReason("manual");
              setShowAddModal(true);
            }}
          >
            <FontAwesomeIcon icon={faPlus} />
          </Button>
        </div>
      </Form.Group>
      <h6 className="text-muted mt-3 mx-2 mb-3">
        Endereços suprimidos pelo sistema identificados como bounces, reclamações, descadastros ou adicionados manualmente
      </h6>

      <div className="pb-3">
        <ColumnSortingTable
          key={"columnSuppressions" + suppressions.length}
          columns={columns}
          title={dataTable.title + " - supressões"}
          data={suppressions}
          getCellProps={(cellinfo) => ({
            className: cellinfo.column.id === "icons" ? "table-icons" : "",
          })}
          triggerExport={triggerExport}
          setTriggerExport={setTriggerExport}
        />
      </div>

      {/* Add suppression modal */}
      <Modal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        centered
        backdrop="static"
        className="nested-modal"
        backdropClassName="nested-modal-backdrop"
      >
        <Form onSubmit={handleAddManual}>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={faBan} className="me-2" />
              Adicionar à lista de supressão
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">E-mail</Form.Label>
              <Form.Control
                type="email"
                placeholder="exemplo@email.com"
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
                autoFocus
              />
            </Form.Group>
            <Form.Group>
              <Form.Label className="fw-semibold">Motivo</Form.Label>
              <Form.Select
                value={addReason}
                onChange={(e) => setAddReason(e.target.value)}
              >
                <option value="manual">Manual</option>
                <option value="unsubscribed">Descadastrado (unsubscribed)</option>
                <option value="bounced">Rejeitado (bounced)</option>
                <option value="complained">Reclamação/Spam (complained)</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Selecione o motivo da supressão.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={() => setShowAddModal(false)}
              disabled={addSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={addSubmitting || !addEmail.trim()}
            >
              {addSubmitting ? "Adicionando..." : "Adicionar"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default EventEmails;
